import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";

import { 
  Users, 
  Clock, 
  CheckCircle, 
  FileText,
  FileCheck,
  Trash2,
  RefreshCw,
  Lock,
  LogOut,
  Download,
  Upload,
  Search,
  Mail,
  MailCheck,
  MailX,
  AlertCircle,
  CalendarDays,
  Star,
  MessageSquare,
  Plus,
  Edit,
  Eye,
  EyeOff,
  RotateCcw,
  GitMerge,
  ExternalLink,
  DollarSign,
  X,
  FolderOpen
} from "lucide-react";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const statusStyles = {
    "Intake Received": "status-badge status-pending",
    "In Preparation": "status-badge status-in_review",
    "Filed": "status-badge status-filed",
    "Completed": "status-badge status-completed",
    "Duplicate – Do Not Use": "status-badge bg-red-100 text-red-800 border-red-200",
    "Archived – Superseded": "status-badge bg-slate-100 text-slate-600 border-slate-200",
    "Merged – Archived": "status-badge bg-purple-100 text-purple-800 border-purple-200",
    "Master File – Active": "status-badge bg-green-100 text-green-800 border-green-200",
  };

  const statusLabels = {
    "Intake Received": "Intake Received",
    "In Preparation": "In Preparation",
    "Filed": "Filed",
    "Completed": "Completed",
    "Duplicate – Do Not Use": "⚠️ Duplicate",
    "Archived – Superseded": "📁 Archived",
    "Merged – Archived": "🔀 Merged",
    "Master File – Active": "✅ Master File",
  };

  return (
    <span className={statusStyles[status] || "status-badge"} data-testid={`status-badge-${status}`}>
      {statusLabels[status] || status}
    </span>
  );
};

// Review flagging removed per request.

// Admin Login Component
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Verify credentials with backend
      await axios.get(`${API}/admin/verify`, {
        auth: { username, password }
      });
      
      // Store credentials in session
      sessionStorage.setItem('adminAuth', btoa(`${username}:${password}`));
      onLogin({ username, password });
      toast.success("Welcome to Admin Dashboard");
    } catch (err) {
      setError("Invalid credentials. Please try again.");
      toast.error("Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4" data-testid="admin-login">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-navy rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-gold" />
            </div>
            <CardTitle className="text-2xl font-heading text-navy">
              Admin Dashboard
            </CardTitle>
            <p className="text-slate-500 text-sm mt-2">
              Enter your credentials to access the admin panel
            </p>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="username" className="text-sm font-medium text-slate-700">Username</Label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="pl-10"
                    placeholder="Enter username"
                    required
                    data-testid="admin-username"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    placeholder="Enter password"
                    required
                    data-testid="admin-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-navy text-white hover:bg-navy-light h-12 font-semibold"
                disabled={loading}
                data-testid="admin-login-btn"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Lock className="w-4 h-4 mr-2" />
                )}
                {loading ? "Authenticating..." : "Access Dashboard"}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <a href="/" className="text-sm text-navy hover:underline inline-flex items-center gap-1">
                ← Back to Home
              </a>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );



};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [intakes, setIntakes] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    intake_received: 0,
    in_preparation: 0,
    filed: 0,
    completed: 0,
  });
  const [loading, setLoading] = useState(true);
  const [selectedIntake, setSelectedIntake] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("newest");
  const [intakePage, setIntakePage] = useState(1);
  const [appointmentsPage, setAppointmentsPage] = useState(1);
  const pageSize = 10;
  const appointmentPageSize = 5;

  const [currentView, setCurrentView] = useState("dashboard");
  const [statusFilter, setStatusFilter] = useState("total");
  const [appointments, setAppointments] = useState([]);
  const [deletedAppointments, setDeletedAppointments] = useState([]);
  const [deletedIntakes, setDeletedIntakes] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [rescheduleCalendarOpen, setRescheduleCalendarOpen] = useState(false);
  const [intakeDocuments, setIntakeDocuments] = useState([]); // Documents for selected intake

  const [referrals, setReferrals] = useState([]);
  const [referralStatusByIntake, setReferralStatusByIntake] = useState({});
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [selectedAppointmentIds, setSelectedAppointmentIds] = useState(new Set());
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleAppt, setRescheduleAppt] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState(null); // Date object
  const [rescheduleTime, setRescheduleTime] = useState("10:00");
  const [savingReschedule, setSavingReschedule] = useState(false);
  const [totals, setTotals] = useState({ total_fees: 0, total_referral_rewards: 0 });
  const [editingFeeId, setEditingFeeId] = useState(null);
  const [editingFeeValue, setEditingFeeValue] = useState("");

  // Fee Tracker State
  const [feeEntries, setFeeEntries] = useState([]);
  const [feeFormData, setFeeFormData] = useState({
    client_name: "",
    fee_amount: "",
    referral_name: "",
    referral_amount: "",
    notes: ""
  });
  const [savingFeeEntry, setSavingFeeEntry] = useState(false);
  const [feeSummary, setFeeSummary] = useState({ total_fees: 0, total_referrals: 0, entry_count: 0, refund_received_fees: 0, refund_received_count: 0 });
  const [feeSearchQuery, setFeeSearchQuery] = useState("");
  const [refundDatePickerOpen, setRefundDatePickerOpen] = useState(null); // entry id or null
  const [feeCurrentPage, setFeeCurrentPage] = useState(1);
  const feePageSize = 10;
  
  // Batch Payment State
  const [batchPaymentOpen, setBatchPaymentOpen] = useState(false);
  const [batchPaymentDate, setBatchPaymentDate] = useState(null);
  const [selectedFeeEntryIds, setSelectedFeeEntryIds] = useState(new Set());
  const [processingBatchPayment, setProcessingBatchPayment] = useState(false);
  
  // Payment Batches State
  const [paymentBatches, setPaymentBatches] = useState([]);
  const [editingBatchId, setEditingBatchId] = useState(null);
  const [editingBatchDate, setEditingBatchDate] = useState(null);

  const [selectedIntakeIds, setSelectedIntakeIds] = useState(new Set());
  const [exportPreviewOpen, setExportPreviewOpen] = useState(false);
  const [exportPreviewData, setExportPreviewData] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);

  // New Appointment Scheduling from Calendar
  const [newApptModalOpen, setNewApptModalOpen] = useState(false);
  const [newApptDate, setNewApptDate] = useState(null);
  const [newApptTime, setNewApptTime] = useState("10:00 AM");
  const [newApptClientName, setNewApptClientName] = useState("");
  const [newApptClientEmail, setNewApptClientEmail] = useState("");
  const [newApptClientPhone, setNewApptClientPhone] = useState("");
  const [savingNewAppt, setSavingNewAppt] = useState(false);
  const [calendarSelectedDate, setCalendarSelectedDate] = useState(new Date());
  const [calendarNotes, setCalendarNotes] = useState("");

  // Reviews Management State
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [reviewForm, setReviewForm] = useState({
    name: "",
    location: "",
    rating: 5,
    review: "",
    service: "Individual Tax Preparation",
    is_featured: true
  });
  const [savingReview, setSavingReview] = useState(false);
  const [portalUsersCount, setPortalUsersCount] = useState(0);
  const [portalUsers, setPortalUsers] = useState([]);
  const [loadingPortalUsers, setLoadingPortalUsers] = useState(false);
  
  // Duplicate Detection State
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  
  // Merge State
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergePreview, setMergePreview] = useState(null);
  const [merging, setMerging] = useState(false);
  const [selectedMergeSource, setSelectedMergeSource] = useState(null);

  // Backup Confirmation Checklist State
  const [showBackupChecklist, setShowBackupChecklist] = useState(false);
  const [backupChecklistIntake, setBackupChecklistIntake] = useState(null);
  const [backupChecklist, setBackupChecklist] = useState({
    folderCreated: false,
    documentsDownloaded: false,
    documentsUploaded: false,
    filesRenamed: false,
    linkSaved: false,
  });
  const [driveFolderLink, setDriveFolderLink] = useState("");

  // Weekly Backup Reminder State (shows on Fridays)
  const [showBackupReminder, setShowBackupReminder] = useState(() => {
    const today = new Date();
    const isFriday = today.getDay() === 5; // 0=Sunday, 5=Friday
    const dismissedDate = sessionStorage.getItem('backupReminderDismissed');
    const todayStr = today.toDateString();
    // Show if it's Friday AND not already dismissed today
    return isFriday && dismissedDate !== todayStr;
  });

  const dismissBackupReminder = () => {
    sessionStorage.setItem('backupReminderDismissed', new Date().toDateString());
    setShowBackupReminder(false);
  };

  // Contact Editing State
  const [editingContact, setEditingContact] = useState(false);
  const [contactForm, setContactForm] = useState({
    phone: "",
    email: "",
    spouse_phone: "",
    spouse_email: ""
  });
  const [savingContact, setSavingContact] = useState(false);

  const estDateTime = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("en-US", {
        timeZone: "America/New_York",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }) + " EST";
    } catch {
      return String(iso);
    }
  };


  const appointmentSlots = [
    "10:00 AM",
    "10:40 AM",
    "11:20 AM",
    "12:00 PM",
    "12:40 PM",
    "1:20 PM",
    "2:00 PM",
    "2:40 PM",
    "3:20 PM",
    "4:00 PM",
    "4:40 PM",
    "5:20 PM",
  ];


  // Check for existing auth on mount
  useEffect(() => {
    const storedAuth = sessionStorage.getItem('adminAuth');
    if (storedAuth) {
      try {
        const decoded = atob(storedAuth);
        const [username, password] = decoded.split(':');
        setCredentials({ username, password });
        setIsAuthenticated(true);
      } catch {
        sessionStorage.removeItem('adminAuth');
      }
    } else {
      setLoading(false);
    }
  }, []);

  const getAuthConfig = useCallback(() => {
    if (!credentials) return {};
    return {
      auth: {
        username: credentials.username,
        password: credentials.password
      }
    };
  }, [credentials]);

  const fetchData = useCallback(async () => {
    if (!credentials) return;
    
    setLoading(true);
    try {
      const config = getAuthConfig();
      const [intakesRes, statsRes, apptsRes, refsRes, deletedApptsRes, deletedIntakesRes, totalsRes, reviewsRes, portalUsersRes] = await Promise.all([
        axios.get(`${API}/admin/intake`, config),
        axios.get(`${API}/admin/stats`, config),
        axios.get(`${API}/admin/appointments`, config),
        axios.get(`${API}/admin/referrals`, config),
        axios.get(`${API}/admin/deleted/appointments`, config),
        axios.get(`${API}/admin/deleted/intakes`, config),
        axios.get(`${API}/admin/stats/totals`, config),
        axios.get(`${API}/admin/reviews`, config),
        axios.get(`${API}/admin/portal-users/count`, config).catch(() => ({ data: { count: 0 } })),
      ]);
      setIntakes(intakesRes.data);
      setDeletedAppointments(deletedApptsRes.data || []);
      setDeletedIntakes(deletedIntakesRes.data || []);
      setTotals(totalsRes.data || { total_fees: 0, total_referral_rewards: 0 });
      setReviews(reviewsRes.data || []);
      setPortalUsersCount(portalUsersRes.data?.count || 0);

      setStats(statsRes.data);
      const apptsData = apptsRes.data || [];
      const refsData = refsRes.data || [];

      setAppointments(apptsData);
      setReferrals(refsData);

      // Build a quick lookup: intake_id -> payout_status
      const map = {};
      for (const r of refsData) {
        if (r?.intake_id) {
          map[r.intake_id] = r.payout_status || "Pending";
        }
      }
      setReferralStatusByIntake(map);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to fetch data");
      }
    } finally {
      setLoading(false);
    }
  }, [credentials, getAuthConfig]);

  // Fetch portal users list
  const fetchPortalUsers = useCallback(async () => {
    if (!credentials) return;
    setLoadingPortalUsers(true);
    try {
      const config = getAuthConfig();
      const res = await axios.get(`${API}/admin/portal-users`, config);
      setPortalUsers(res.data || []);
    } catch (error) {
      toast.error("Failed to fetch portal users");
    } finally {
      setLoadingPortalUsers(false);
    }
  }, [credentials, getAuthConfig]);

  // Fetch fee entries
  const fetchFeeEntries = useCallback(async () => {
    if (!credentials) return;
    try {
      const config = getAuthConfig();
      const [entriesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/admin/fee-entries`, config),
        axios.get(`${API}/admin/fee-entries/summary`, config)
      ]);
      setFeeEntries(entriesRes.data || []);
      setFeeSummary(summaryRes.data || { total_fees: 0, total_referrals: 0, entry_count: 0 });
    } catch (error) {
      console.error("Failed to fetch fee entries:", error);
    }
  }, [credentials, getAuthConfig]);

  // Add new fee entry
  const addFeeEntry = async () => {
    if (!feeFormData.client_name.trim()) {
      toast.error("Please enter a client name");
      return;
    }
    setSavingFeeEntry(true);
    try {
      const config = getAuthConfig();
      await axios.post(`${API}/admin/fee-entries`, {
        client_name: feeFormData.client_name.trim(),
        fee_amount: parseFloat(feeFormData.fee_amount) || 0,
        referral_name: feeFormData.referral_name.trim(),
        referral_amount: parseFloat(feeFormData.referral_amount) || 0,
        notes: feeFormData.notes.trim()
      }, config);
      toast.success("Fee entry added");
      setFeeFormData({ client_name: "", fee_amount: "", referral_name: "", referral_amount: "", notes: "" });
      fetchFeeEntries();
    } catch (error) {
      toast.error("Failed to add fee entry");
    } finally {
      setSavingFeeEntry(false);
    }
  };

  // Delete fee entry
  const deleteFeeEntry = async (entryId) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;
    try {
      await axios.delete(`${API}/admin/fee-entries/${entryId}`, getAuthConfig());
      toast.success("Entry deleted");
      fetchFeeEntries();
    } catch (error) {
      toast.error("Failed to delete entry");
    }
  };

  // Edit fee entry state
  const [editingFeeEntry, setEditingFeeEntry] = useState(null);
  const [editFeeFormData, setEditFeeFormData] = useState({
    client_name: "",
    fee_amount: "",
    referral_name: "",
    referral_amount: "",
    notes: ""
  });

  // Start editing a fee entry
  const startEditFeeEntry = (entry) => {
    setEditingFeeEntry(entry.id);
    setEditFeeFormData({
      client_name: entry.client_name || "",
      fee_amount: entry.fee_amount?.toString() || "",
      referral_name: entry.referral_name || "",
      referral_amount: entry.referral_amount?.toString() || "",
      notes: entry.notes || ""
    });
  };

  // Cancel editing
  const cancelEditFeeEntry = () => {
    setEditingFeeEntry(null);
    setEditFeeFormData({ client_name: "", fee_amount: "", referral_name: "", referral_amount: "", notes: "" });
  };

  // Save edited fee entry
  const saveEditFeeEntry = async (entryId) => {
    if (!editFeeFormData.client_name.trim()) {
      toast.error("Client name is required");
      return;
    }
    try {
      await axios.put(`${API}/admin/fee-entries/${entryId}`, {
        client_name: editFeeFormData.client_name.trim(),
        fee_amount: parseFloat(editFeeFormData.fee_amount) || 0,
        referral_name: editFeeFormData.referral_name.trim(),
        referral_amount: parseFloat(editFeeFormData.referral_amount) || 0,
        notes: editFeeFormData.notes.trim(),
        refund_received: editFeeFormData.refund_received || false
      }, getAuthConfig());
      toast.success("Entry updated");
      setEditingFeeEntry(null);
      fetchFeeEntries();
    } catch (error) {
      toast.error("Failed to update entry");
    }
  };

  // Toggle refund received status
  const toggleRefundStatus = async (entryId) => {
    try {
      await axios.patch(`${API}/admin/fee-entries/${entryId}/refund-status`, {}, getAuthConfig());
      fetchFeeEntries();
    } catch (error) {
      toast.error("Failed to update refund status");
    }
  };

  // Update refund date
  const updateRefundDate = async (entryId, date) => {
    try {
      const dateStr = date ? date.toISOString() : null;
      await axios.patch(`${API}/admin/fee-entries/${entryId}/refund-date`, {
        refund_received_date: dateStr
      }, getAuthConfig());
      setRefundDatePickerOpen(null);
      fetchFeeEntries();
      toast.success("Refund date updated");
    } catch (error) {
      toast.error("Failed to update refund date");
    }
  };

  // Clear refund date
  const clearRefundDate = async (entryId) => {
    try {
      await axios.patch(`${API}/admin/fee-entries/${entryId}/refund-date`, {
        refund_received_date: null
      }, getAuthConfig());
      setRefundDatePickerOpen(null);
      fetchFeeEntries();
      toast.success("Refund date cleared");
    } catch (error) {
      toast.error("Failed to clear refund date");
    }
  };

  // Toggle selection for batch payment
  const toggleFeeEntrySelection = (entryId) => {
    setSelectedFeeEntryIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entryId)) {
        newSet.delete(entryId);
      } else {
        newSet.add(entryId);
      }
      return newSet;
    });
  };

  // Select all unpaid entries
  const selectAllUnpaidEntries = () => {
    const unpaidIds = feeEntries
      .filter(e => !e.refund_received)
      .map(e => e.id);
    setSelectedFeeEntryIds(new Set(unpaidIds));
  };

  // Clear selection
  const clearFeeEntrySelection = () => {
    setSelectedFeeEntryIds(new Set());
  };

  // Process batch payment - creates a new batch
  const processBatchPayment = async () => {
    if (selectedFeeEntryIds.size === 0) {
      toast.error("Please select at least one client");
      return;
    }
    if (!batchPaymentDate) {
      toast.error("Please select a payment date");
      return;
    }

    setProcessingBatchPayment(true);
    try {
      const response = await axios.post(`${API}/admin/payment-batches`, {
        entry_ids: Array.from(selectedFeeEntryIds),
        payment_date: batchPaymentDate.toISOString()
      }, getAuthConfig());
      
      toast.success(`Created Batch ${response.data.batch_number} with ${response.data.entry_count} clients!`);
      setBatchPaymentOpen(false);
      setBatchPaymentDate(null);
      setSelectedFeeEntryIds(new Set());
      fetchFeeEntries();
      fetchPaymentBatches();
    } catch (error) {
      toast.error("Failed to create batch");
    } finally {
      setProcessingBatchPayment(false);
    }
  };

  // Fetch payment batches
  const fetchPaymentBatches = useCallback(async () => {
    if (!credentials) return;
    try {
      const config = getAuthConfig();
      const res = await axios.get(`${API}/admin/payment-batches`, config);
      setPaymentBatches(res.data || []);
    } catch (error) {
      console.error("Failed to fetch payment batches:", error);
    }
  }, [credentials, getAuthConfig]);

  // Update batch payment date
  const updateBatchDate = async (batchId) => {
    if (!editingBatchDate) {
      toast.error("Please select a date");
      return;
    }
    try {
      await axios.put(`${API}/admin/payment-batches/${batchId}`, {
        payment_date: editingBatchDate.toISOString()
      }, getAuthConfig());
      toast.success("Batch date updated");
      setEditingBatchId(null);
      setEditingBatchDate(null);
      fetchPaymentBatches();
      fetchFeeEntries();
    } catch (error) {
      toast.error("Failed to update batch date");
    }
  };

  // Delete a batch
  const deleteBatch = async (batchId, batchNumber) => {
    if (!window.confirm(`Are you sure you want to delete Batch ${batchNumber}? This will unmark all clients in this batch as unpaid.`)) return;
    try {
      await axios.delete(`${API}/admin/payment-batches/${batchId}`, getAuthConfig());
      toast.success(`Batch ${batchNumber} deleted`);
      fetchPaymentBatches();
      fetchFeeEntries();
    } catch (error) {
      toast.error("Failed to delete batch");
    }
  };

  // Fetch portal users when switching to that view
  useEffect(() => {
    if (currentView === "portal-users" && credentials) {
      fetchPortalUsers();
    }
  }, [currentView, credentials, fetchPortalUsers]);

  // Fetch fee entries and batches when switching to fee-tracker view
  useEffect(() => {
    if (currentView === "fee-tracker" && credentials) {
      fetchFeeEntries();
      fetchPaymentBatches();
    }
  }, [currentView, credentials, fetchFeeEntries, fetchPaymentBatches]);
  
  // Fetch payment batches when switching to batch-folders view
  useEffect(() => {
    if (currentView === "batch-folders" && credentials) {
      fetchPaymentBatches();
    }
  }, [currentView, credentials, fetchPaymentBatches]);

  useEffect(() => {
    if (isAuthenticated && credentials) {
      fetchData();
    }
  }, [isAuthenticated, credentials, fetchData]);

  // Reset fee pagination when search changes
  useEffect(() => {
    setFeeCurrentPage(1);
  }, [feeSearchQuery]);

  const handleLogin = (creds) => {
    setCredentials(creds);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    setIsAuthenticated(false);
    setCredentials(null);
    setIntakes([]);
    setStats({ total: 0, pending: 0, in_review: 0, completed: 0 });
  };

  // Delete portal user
  const deletePortalUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this portal user account?")) return;
    try {
      await axios.delete(`${API}/admin/portal-users/${userId}`, getAuthConfig());
      toast.success("Portal user deleted");
      fetchPortalUsers();
      fetchData(); // Refresh count
    } catch (error) {
      toast.error("Failed to delete portal user");
    }
  };

  // Delete document (admin)
  const deleteDocument = async (documentId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;
    try {
      await axios.delete(`${API}/admin/documents/${documentId}`, getAuthConfig());
      toast.success("Document deleted");
      // Refresh documents for selected intake
      if (selectedIntake) {
        viewDetails(selectedIntake);
      }
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  // Upload document for client (admin)
  const uploadDocumentForClient = async (intakeId, file, docType) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("doc_type", docType || "other");
    
    try {
      // Use basic auth config for upload
      await axios.post(`${API}/admin/intake/${intakeId}/documents/upload`, formData, getAuthConfig());
      toast.success("Document uploaded successfully");
      // Refresh documents
      if (selectedIntake && selectedIntake.id === intakeId) {
        const response = await axios.get(
          `${API}/admin/intake/${intakeId}/documents`,
          getAuthConfig()
        );
        setIntakeDocuments(response.data || []);
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload document");
    }
  };

  // ======================= MERGE FUNCTIONS =======================
  
  // Preview merge
  const previewMerge = async (sourceIntakeId) => {
    if (!selectedIntake) return;
    
    try {
      const response = await axios.get(
        `${API}/admin/intake/${selectedIntake.id}/merge-preview/${sourceIntakeId}`,
        getAuthConfig()
      );
      setMergePreview(response.data);
      setSelectedMergeSource(sourceIntakeId);
      setShowMergeModal(true);
    } catch (error) {
      toast.error("Failed to load merge preview");
    }
  };

  // Execute merge
  const executeMerge = async () => {
    if (!selectedIntake || !selectedMergeSource) return;
    
    setMerging(true);
    try {
      const response = await axios.post(
        `${API}/admin/intake/merge`,
        {
          master_intake_id: selectedIntake.id,
          source_intake_id: selectedMergeSource,
          merge_personal_info: true,
          merge_tax_details: true,
          merge_documents: true,
          merge_notes: true
        },
        getAuthConfig()
      );
      
      toast.success(`Merged & consolidated into 1 file! ${response.data.documents_moved} documents moved.`);
      setShowMergeModal(false);
      setMergePreview(null);
      setSelectedMergeSource(null);
      setDialogOpen(false); // Close details modal
      setSelectedIntake(null); // Clear selected intake
      
      // Refresh data
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to merge intakes");
    } finally {
      setMerging(false);
    }
  };

  // ======================= REVIEWS MANAGEMENT FUNCTIONS =======================
  const serviceOptions = [
    "Individual Tax Preparation",
    "Business Tax Preparation",
    "Self-Employed Tax Services",
    "Credit Education & Tax Prep",
    "Credit Education & Coaching",
    "1099 Tax Services"
  ];

  const openReviewModal = (review = null) => {
    if (review) {
      setEditingReview(review);
      setReviewForm({
        name: review.name || "",
        location: review.location || "",
        rating: review.rating || 5,
        review: review.review || "",
        service: review.service || "Individual Tax Preparation",
        is_featured: review.is_featured !== false
      });
    } else {
      setEditingReview(null);
      setReviewForm({
        name: "",
        location: "",
        rating: 5,
        review: "",
        service: "Individual Tax Preparation",
        is_featured: true
      });
    }
    setReviewModalOpen(true);
  };

  const closeReviewModal = () => {
    setReviewModalOpen(false);
    setEditingReview(null);
    setReviewForm({
      name: "",
      location: "",
      rating: 5,
      review: "",
      service: "Individual Tax Preparation",
      is_featured: true
    });
  };

  const saveReview = async () => {
    if (!reviewForm.name || !reviewForm.location || !reviewForm.review) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setSavingReview(true);
    try {
      const config = getAuthConfig();
      if (editingReview) {
        await axios.put(`${API}/admin/reviews/${editingReview.id}`, reviewForm, config);
        toast.success("Review updated successfully");
      } else {
        await axios.post(`${API}/admin/reviews`, reviewForm, config);
        toast.success("Review created successfully");
      }
      closeReviewModal();
      fetchData();
    } catch (error) {
      toast.error(editingReview ? "Failed to update review" : "Failed to create review");
    } finally {
      setSavingReview(false);
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm("Are you sure you want to delete this review?")) return;
    
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`, getAuthConfig());
      toast.success("Review deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete review");
    }
  };

  const toggleReviewFeatured = async (reviewId) => {
    try {
      const res = await axios.patch(`${API}/admin/reviews/${reviewId}/toggle-featured`, {}, getAuthConfig());
      toast.success(res.data.is_featured ? "Review is now featured" : "Review is no longer featured");
      fetchData();
    } catch (error) {
      toast.error("Failed to toggle featured status");
    }
  };

  const restoreReview = async (reviewId) => {
    try {
      await axios.patch(`${API}/admin/reviews/${reviewId}/restore`, {}, getAuthConfig());
      toast.success("Review restored");
      fetchData();
    } catch (error) {
      toast.error("Failed to restore review");
    }
  };
  // ======================= END REVIEWS MANAGEMENT =======================

  // Reset backup checklist
  const resetBackupChecklist = () => {
    setBackupChecklist({
      folderCreated: false,
      documentsDownloaded: false,
      documentsUploaded: false,
      filesRenamed: false,
      linkSaved: false,
    });
    setDriveFolderLink("");
  };

  // Check if all backup items are checked
  const isBackupComplete = () => {
    return (
      backupChecklist.folderCreated &&
      backupChecklist.documentsDownloaded &&
      backupChecklist.documentsUploaded &&
      backupChecklist.filesRenamed &&
      backupChecklist.linkSaved &&
      driveFolderLink.trim().length > 0
    );
  };

  // Proceed with status change to In Preparation after backup checklist
  const proceedToInPreparation = async () => {
    if (!isBackupComplete()) {
      toast.error("Please complete all backup checklist items before proceeding");
      return;
    }

    if (!backupChecklistIntake) return;

    try {
      // Save backup confirmation to the intake
      await axios.patch(
        `${API}/admin/intake/${backupChecklistIntake.id}/status`,
        { status: "Filed" },
        getAuthConfig()
      );
      
      // Save backup metadata
      await axios.patch(
        `${API}/admin/intake/${backupChecklistIntake.id}/note`,
        { 
          note: `[BACKUP CONFIRMED ${new Date().toLocaleDateString()}]\nGoogle Drive Link: ${driveFolderLink}\n\n${backupChecklistIntake.documents?.additional_notes || ""}`
        },
        getAuthConfig()
      );

      toast.success("Status updated to Filed - Backup confirmed!");
      setShowBackupChecklist(false);
      setBackupChecklistIntake(null);
      resetBackupChecklist();
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to update status");
      }
    }
  };

  const updateStatus = async (intakeId, newStatus) => {
    // If changing to "Filed", show backup checklist first (moved from "In Preparation")
    if (newStatus === "Filed") {
      const intake = intakes.find(i => i.id === intakeId);
      if (intake) {
        // Check if backup was already confirmed
        const notes = intake.documents?.additional_notes || "";
        const backupAlreadyConfirmed = notes.includes("BACKUP CONFIRMED") && notes.toLowerCase().includes("drive.google.com");
        
        if (!backupAlreadyConfirmed) {
          // Show backup checklist modal
          setBackupChecklistIntake(intake);
          resetBackupChecklist();
          setShowBackupChecklist(true);
          return; // Don't proceed until checklist is complete
        }
        
        // Backup already confirmed, proceed with filing safety check
        try {
          const checklistRes = await axios.get(
            `${API}/admin/filing-checklist/${intakeId}`,
            getAuthConfig()
          );
          const checklist = checklistRes.data;
          
          if (!checklist.checks.only_one_active_intake || !checklist.checks.no_duplicate_portal_accounts) {
            // FILING BLOCKED - duplicates found
            const issues = [];
            if (!checklist.checks.only_one_active_intake) {
              issues.push(`• ${checklist.details.other_active_intakes_count} other active intake(s) found for this client`);
            }
            if (!checklist.checks.no_duplicate_portal_accounts) {
              issues.push(`• ${checklist.details.duplicate_portal_accounts_count} duplicate portal account(s) found`);
            }
            
            toast.error(
              <div>
                <strong>🚫 Filing blocked — duplicate records found.</strong>
                <div className="mt-2 text-sm">
                  {issues.map((issue, i) => <div key={i}>{issue}</div>)}
                </div>
                <div className="mt-2 text-xs text-amber-200">
                  Golden Rule: One client. One intake. One return.
                </div>
              </div>,
              { duration: 8000 }
            );
            return; // HARD BLOCK - cannot proceed
          }
          
          // All checks passed, confirm before filing
          if (!window.confirm(`✅ All safety checks passed for ${checklist.client_name}.\n\nThis will send a 'Filed' confirmation email to the client.\n\nConfirm filing status change?`)) {
            return;
          }
        } catch (error) {
          console.error("Checklist error:", error);
          toast.error("Could not verify filing checklist. Please try again.");
          return;
        }
      }
    }
    
    // If marking as duplicate or archived, add confirmation
    if (newStatus === "Duplicate – Do Not Use" || newStatus === "Archived – Superseded") {
      const action = newStatus === "Duplicate – Do Not Use" ? "mark as DUPLICATE" : "ARCHIVE";
      if (!window.confirm(`Are you sure you want to ${action} this intake?\n\nThis will hide it from active client list.`)) {
        return;
      }
    }
    
    try {
      await axios.patch(
        `${API}/admin/intake/${intakeId}/status`,
        { status: newStatus },
        getAuthConfig()
      );
      toast.success("Status updated successfully");
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to update status");
      }
    }
  };

  const updateReferralReward = async (intake, payoutStatus) => {
    try {
      await axios.patch(
        `${API}/admin/intake/${intake.id}/referral-reward`,
        { referral_reward_status: payoutStatus },
        getAuthConfig()
      );
      toast.success("Referral reward status updated");
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to update referral reward status");
      }
    }
  };

  const updateFee = async (intakeId, fee) => {
    try {
      const feeValue = parseFloat(fee);
      if (isNaN(feeValue) || feeValue < 0) {
        toast.error("Please enter a valid fee amount");
        return;
      }
      await axios.patch(
        `${API}/admin/intake/${intakeId}/fee`,
        { fee: feeValue },
        getAuthConfig()
      );
      toast.success("Fee updated");
      setEditingFeeId(null);
      setEditingFeeValue("");
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to update fee");
      }
    }
  };

  const startEditingFee = (intake) => {
    setEditingFeeId(intake.id);
    setEditingFeeValue(intake.fee !== null && intake.fee !== undefined ? String(intake.fee) : "");
  };

  const cancelEditingFee = () => {
    setEditingFeeId(null);
    setEditingFeeValue("");
  };

  const deleteIntake = async (intakeId) => {
    if (!window.confirm("Are you sure you want to delete this submission?")) return;

    try {
      await axios.delete(`${API}/admin/intake/${intakeId}`, getAuthConfig());
      toast.success("Submission deleted");
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to delete submission");
      }
    }
  };

  const restoreDeletedAppointment = async (appointmentId) => {
    try {
      await axios.post(`${API}/admin/deleted/appointments/${appointmentId}/restore`, {}, getAuthConfig());
      toast.success("Appointment restored");
      fetchData();
    } catch {
      toast.error("Failed to restore appointment");
    }
  };

  const restoreDeletedIntake = async (intakeId) => {
    try {
      await axios.post(`${API}/admin/deleted/intakes/${intakeId}/restore`, {}, getAuthConfig());
      toast.success("Client restored");
      fetchData();
    } catch {
      toast.error("Failed to restore client");
    }
  };

  const permanentlyDeleteAppointment = async (appointmentId) => {
    const ok = window.confirm("Permanently delete this appointment? This cannot be undone.");
    if (!ok) return;
    try {
      await axios.delete(`${API}/admin/deleted/appointments/${appointmentId}`, getAuthConfig());
      toast.success("Appointment permanently deleted");
      fetchData();
    } catch {
      toast.error("Failed to permanently delete appointment");
    }
  };

  const permanentlyDeleteIntake = async (intakeId) => {
    const ok = window.confirm("Permanently delete this client submission? This cannot be undone.");
    if (!ok) return;
    try {
      await axios.delete(`${API}/admin/deleted/intakes/${intakeId}`, getAuthConfig());
      toast.success("Client permanently deleted");
      fetchData();
    } catch {
      toast.error("Failed to permanently delete client");
    }
  };

  // Hide single-delete UI (bulk delete + Deleted box is the new flow)
  void deleteIntake;

  const toggleAppointmentSelection = (apptId, checked) => {
    setSelectedAppointmentIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(apptId);
      else next.delete(apptId);
      return next;
    });
  };

  const toggleSelectAllAppointments = (checked) => {
    setSelectedAppointmentIds(() => {
      if (!checked) return new Set();
      return new Set((appointments || []).map((a) => a.id));
    });
  };

  const deleteSelectedAppointments = async () => {
    const ids = Array.from(selectedAppointmentIds);
    if (ids.length === 0) {
      toast.error("No appointments selected");
      return;
    }

    const ok = window.confirm(`Delete ${ids.length} selected appointment(s)?`);
    if (!ok) return;

    try {
      await axios.post(`${API}/admin/appointments/bulk-delete`, { appointment_ids: ids }, getAuthConfig());
      toast.success("Appointments deleted");
      setSelectedAppointmentIds(new Set());
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to delete appointments");
      }
    }
  };


  const toggleIntakeSelection = (intakeId, checked) => {
    setSelectedIntakeIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(intakeId);
      else next.delete(intakeId);
      return next;
    });
  };

  const deleteSelectedIntakes = async () => {
    const ids = Array.from(selectedIntakeIds);
    if (ids.length === 0) {
      toast.error("No clients selected");
      return;
    }

    const ok = window.confirm(`Delete ${ids.length} selected client submission(s)?`);
    if (!ok) return;

    try {
      await axios.post(`${API}/admin/intake/bulk-delete`, { intake_ids: ids }, getAuthConfig());
      toast.success("Client submissions deleted");
      setSelectedIntakeIds(new Set());
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to delete client submissions");
      }
    }
  };

  const openRescheduleModal = (appt) => {
    setRescheduleAppt(appt);
    setRescheduleDate(appt?.start_time ? new Date(appt.start_time) : new Date());
    setRescheduleCalendarOpen(false);
    setRescheduleOpen(true);
    setRescheduleTime("10:00");
    setRescheduleOpen(true);
  };

  const saveReschedule = async () => {
    if (!rescheduleAppt || !rescheduleDate) return;

    setSavingReschedule(true);
    try {
      const yyyy = rescheduleDate.getFullYear();
      const mm = String(rescheduleDate.getMonth() + 1).padStart(2, "0");
      const dd = String(rescheduleDate.getDate()).padStart(2, "0");
      const dateStr = `${yyyy}-${mm}-${dd}`;

      const time12To24 = (t) => {
        if (!t) return "";
        const m = String(t).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
        if (!m) return String(t).trim();
        let hour = parseInt(m[1], 10);
        const minute = m[2];
        const ampm = m[3].toUpperCase();
        if (ampm === "AM") {
          if (hour === 12) hour = 0;
        } else {
          if (hour !== 12) hour += 12;
        }
        return `${String(hour).padStart(2, "0")}:${minute}`;
      };

      const startTime = time12To24(rescheduleTime);

      await axios.patch(
        `${API}/admin/appointments/${rescheduleAppt.id}/reschedule`,
        { date: dateStr, start_time: startTime },
        getAuthConfig()
      );

      toast.success("Appointment rescheduled");
      setRescheduleOpen(false);
      setRescheduleAppt(null);
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to reschedule appointment");
      }
    } finally {
      setSavingReschedule(false);
    }
  };

  // Create new appointment from calendar
  const createNewAppointment = async () => {
    if (!newApptDate || !newApptTime || !newApptClientName || !newApptClientEmail) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setSavingNewAppt(true);
    try {
      // Parse the time to create a proper datetime
      const [time, period] = newApptTime.split(' ');
      let [hours, minutes] = time.split(':').map(Number);
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      
      const startDateTime = new Date(newApptDate);
      startDateTime.setHours(hours, minutes, 0, 0);
      
      const endDateTime = new Date(startDateTime);
      endDateTime.setMinutes(endDateTime.getMinutes() + 30); // 30 min appointment
      
      await axios.post(
        `${API}/admin/appointments`,
        {
          client_name: newApptClientName,
          client_email: newApptClientEmail,
          client_phone: newApptClientPhone,
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          status: "Scheduled",
          notes: calendarNotes || ""
        },
        getAuthConfig()
      );
      
      toast.success("Appointment created successfully");
      setNewApptModalOpen(false);
      setNewApptClientName("");
      setNewApptClientEmail("");
      setNewApptClientPhone("");
      setNewApptTime("10:00 AM");
      setCalendarNotes("");
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to create appointment");
      }
    } finally {
      setSavingNewAppt(false);
    }
  };

  // Generate ICS file for calendar sync
  const generateIcsFile = (appt) => {
    const startDate = new Date(appt.start_time);
    const endDate = new Date(appt.start_time);
    endDate.setMinutes(endDate.getMinutes() + 30);
    
    const formatIcsDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    
    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//WMJ Enterprise Services//Tax Consultation//EN
BEGIN:VEVENT
UID:${appt.id}@wmjenterpriseservices.com
DTSTAMP:${formatIcsDate(new Date())}
DTSTART:${formatIcsDate(startDate)}
DTEND:${formatIcsDate(endDate)}
SUMMARY:Tax Consultation - ${appt.client_name}
DESCRIPTION:Tax consultation with ${appt.client_name}\\nEmail: ${appt.client_email}\\nPhone: ${appt.client_phone || 'N/A'}
LOCATION:WMJ Enterprise Services
STATUS:CONFIRMED
END:VEVENT
END:VCALENDAR`;
    
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `appointment-${appt.client_name.replace(/\s+/g, '-')}.ics`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success("Calendar file downloaded - import it to your calendar app");
  };


  const resendIntakeEmail = async (intakeId) => {
    try {
      // Optimistically set status to pending
      setIntakes((prev) =>
        prev.map((intake) =>
          intake.id === intakeId
            ? {
                ...intake,
                emails: {
                  ...intake.emails,
                  intake: {
                    ...intake.emails?.intake,
                    delivery_status: null, // Reset to pending
                  },
                },
              }
            : intake
        )
      );

      const res = await axios.post(
        `${API}/admin/intake/${intakeId}/resend-intake-email`,
        {},
        getAuthConfig()
      );
      
      if (res.data?.sent) {
        const emailId = res.data?.email_id || "sent";
        toast.success(
          <div>
            <strong>Email Resent Successfully!</strong>
            <p className="text-xs mt-1 opacity-80">Email ID: {emailId}</p>
            <p className="text-xs opacity-70">Status will update when delivery is confirmed.</p>
          </div>,
          { duration: 5000 }
        );
      } else {
        toast.error("Email not sent (provider issue)");
        // Revert optimistic update
        fetchData();
      }
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to resend email");
        // Revert optimistic update
        fetchData();
      }
    }
  };

  const viewDetails = async (intake) => {
    setSelectedIntake(intake);
    setNoteDraft(intake?.documents?.additional_notes || "");
    setDialogOpen(true);
    setDuplicateInfo(null); // Reset duplicate info
    
    // Fetch documents for this intake
    try {
      const response = await axios.get(
        `${API}/admin/intake/${intake.id}/documents`,
        getAuthConfig()
      );
      setIntakeDocuments(response.data || []);
    } catch (error) {
      console.error("Failed to fetch documents:", error);
      setIntakeDocuments([]);
    }
    
    // Fetch duplicate detection info
    try {
      const dupResponse = await axios.get(
        `${API}/admin/intake/${intake.id}/check-duplicates`,
        getAuthConfig()
      );
      setDuplicateInfo(dupResponse.data);
    } catch (error) {
      console.error("Failed to check duplicates:", error);
    }
  };

  const saveNote = async () => {
    if (!selectedIntake) return;
    setSavingNote(true);
    try {
      await axios.patch(
        `${API}/admin/intake/${selectedIntake.id}/note`,
        { note: noteDraft },
        getAuthConfig()
      );
      toast.success("Note saved");
      setSelectedIntake((prev) => ({
        ...prev,
        documents: { ...prev.documents, additional_notes: noteDraft },
      }));

      fetchData();
    } catch {
      toast.error("Failed to save note");
    } finally {
      setSavingNote(false);
    }
  };

  const saveContact = async () => {
    if (!selectedIntake) return;
    setSavingContact(true);
    try {
      await axios.patch(
        `${API}/admin/intake/${selectedIntake.id}/contact`,
        contactForm,
        getAuthConfig()
      );
      toast.success("Contact information updated");
      
      // Update local state
      setSelectedIntake((prev) => ({
        ...prev,
        contact: {
          ...prev.contact,
          phone: contactForm.phone || prev.contact?.phone,
          email: contactForm.email || prev.contact?.email,
        },
        tax_details: {
          ...prev.tax_details,
          spouse_phone: contactForm.spouse_phone || prev.tax_details?.spouse_phone,
          spouse_email: contactForm.spouse_email || prev.tax_details?.spouse_email,
        }
      }));
      
      setEditingContact(false);
      fetchData();
    } catch {
      toast.error("Failed to update contact information");
    } finally {
      setSavingContact(false);
    }
  };

  const startEditingContact = () => {
    setContactForm({
      phone: selectedIntake?.contact?.phone || "",
      email: selectedIntake?.contact?.email || "",
      spouse_phone: selectedIntake?.tax_details?.spouse_phone || "",
      spouse_email: selectedIntake?.tax_details?.spouse_email || "",
    });
    setEditingContact(true);
  };

  const formatDate = (dateString) => {
    return estDateTime(dateString);
  };

  // CSV export with preview
  const openExportPreview = async () => {
    // Use the new export endpoint - exports all clients or filtered
    setExportLoading(true);
    try {
      const url = statusFilter && statusFilter !== "total" 
        ? `${API}/admin/export/clients?status_filter=${encodeURIComponent(statusFilter)}`
        : `${API}/admin/export/clients`;
      
      const res = await axios.get(url, {
        ...getAuthConfig(),
        responseType: "blob",
      });
      
      // Create download link
      const blob = new Blob([res.data], { type: "text/csv" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `wmj_clients_export_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
      
      toast.success("Client data exported to CSV!");
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to export clients");
      }
    } finally {
      setExportLoading(false);
    }
  };

  // Export documents for a single client
  const exportClientDocuments = async (intakeId, clientName) => {
    try {
      toast.info("Preparing documents for download...");
      const res = await axios.get(
        `${API}/admin/export/client/${intakeId}/documents`,
        {
          ...getAuthConfig(),
          responseType: "blob",
        }
      );
      
      const blob = new Blob([res.data], { type: "application/zip" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `${clientName.replace(/\s+/g, "_").toUpperCase()}_${new Date().getFullYear()}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
      
      toast.success(`Documents exported for ${clientName}!`);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("No documents found for this client");
      } else {
        toast.error("Failed to export documents");
      }
    }
  };

  // Export all documents
  const exportAllDocuments = async () => {
    try {
      toast.info("Preparing all documents for download... This may take a moment.");
      const url = statusFilter && statusFilter !== "total"
        ? `${API}/admin/export/all-documents?status_filter=${encodeURIComponent(statusFilter)}`
        : `${API}/admin/export/all-documents`;
      
      const res = await axios.get(url, {
        ...getAuthConfig(),
        responseType: "blob",
      });
      
      const blob = new Blob([res.data], { type: "application/zip" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `wmj_all_documents_${new Date().toISOString().split("T")[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
      
      toast.success("All documents exported!");
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("No documents found");
      } else {
        toast.error("Failed to export documents");
      }
    }
  };

  const downloadCsv = async () => {
    // This function is now handled by openExportPreview
    openExportPreview();
  };


  // Show login if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  // Count clients by service
  const creditRepairCount = intakes.filter((i) => 
    i.tax_details?.services_needed?.includes("credit_repair")
  ).length;
  const fundingCount = intakes.filter((i) => 
    i.tax_details?.services_needed?.includes("funding")
  ).length;

  const statCards = [
    { key: "Intake Received", title: "Intake Received", value: stats.intake_received, icon: Clock, color: "text-amber-600" },
    { key: "In Preparation", title: "In Preparation", value: stats.in_preparation, icon: FileText, color: "text-blue-600" },
    { key: "Completed", title: "Completed", value: stats.completed, icon: CheckCircle, color: "text-green-600" },
    { key: "total", title: "All Clients", value: stats.total, icon: Users, color: "text-navy" },
    { key: "Appointments", title: "Appointments", value: appointments.length, icon: CalendarDays, color: "text-navy" },
    { key: "Reviews", title: "Reviews", value: reviews.length, icon: MessageSquare, color: "text-gold" },
    { key: "PortalUsers", title: "Client Portal Users", value: portalUsersCount, icon: Users, color: "text-purple-600" },
    { key: "FeeTracker", title: "Fee Tracker", value: feeSummary.entry_count, icon: DollarSign, color: "text-emerald-600" },
    { key: "BatchFolders", title: "Batch Folders", value: paymentBatches.length, icon: FolderOpen, color: "text-indigo-600" },
    { key: "Deleted", title: "Deleted", value: (deletedAppointments.length + deletedIntakes.length), icon: Trash2, color: "text-red-600", link: "/admin/deleted" },
  ];

  const sourceIntakes = intakes;

  const filteredIntakes = sourceIntakes.filter((intake) => {
    const term = searchTerm.trim().toLowerCase();

    // Status filter from stat cards
    if (statusFilter && statusFilter !== "total") {
      const s = intake.status || "";
      if (statusFilter === "Completed_Filed") {
        if (s !== "Completed" && s !== "Filed") return false;
      } else if (s !== statusFilter) {
        return false;
      }
    }

    if (!term) return true;

    const firstName = (intake.contact?.first_name || "").toLowerCase();
    const lastName = (intake.contact?.last_name || "").toLowerCase();
    const email = (intake.contact?.email || "").toLowerCase();
    const phone = (intake.contact?.phone || "").toLowerCase();
    const referralName = (intake.contact?.referral_name || "").toLowerCase();
    const status = (intake.status || "").toLowerCase();
    const filingStatus = (intake.tax_details?.filing_status || "").toLowerCase().replace("_", " ");

    return (
      `${firstName} ${lastName}`.includes(term) ||
      firstName.includes(term) ||
      lastName.includes(term) ||
      email.includes(term) ||
      phone.includes(term) ||
      referralName.includes(term) ||
      status.includes(term) ||
      filingStatus.includes(term)
    );
  });

  const sortedIntakes = [...filteredIntakes].sort((a, b) => {
    const aName = `${a?.contact?.first_name || ""} ${a?.contact?.last_name || ""}`.trim().toLowerCase();
    const bName = `${b?.contact?.first_name || ""} ${b?.contact?.last_name || ""}`.trim().toLowerCase();
    const aTime = new Date(a?.created_at || 0).getTime();
    const bTime = new Date(b?.created_at || 0).getTime();

    if (sortOption === "alpha") return aName.localeCompare(bName);
    if (sortOption === "oldest") return aTime - bTime;
    return bTime - aTime; // newest
  });

  const totalIntakePages = Math.max(1, Math.ceil(sortedIntakes.length / pageSize));
  const pagedIntakes = sortedIntakes.slice((intakePage - 1) * pageSize, intakePage * pageSize);

  // Select all visible clients on current page
  const toggleSelectAllIntakes = (checked) => {
    setSelectedIntakeIds((prev) => {
      if (!checked) {
        // Unselect all on current page
        const visibleIds = new Set((pagedIntakes || []).map((i) => i.id));
        const next = new Set(prev);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      }
      // Select all on current page
      const next = new Set(prev);
      (pagedIntakes || []).forEach((i) => next.add(i.id));
      return next;
    });
  };

  // Select ALL filtered results (across all pages)
  const selectAllFiltered = () => {
    const allFilteredIds = new Set((filteredIntakes || []).map((i) => i.id));
    setSelectedIntakeIds(allFilteredIds);
    toast.success(`Selected ${allFilteredIds.size} clients matching your filter`);
  };

  // Clear all selections
  const clearSelection = () => {
    setSelectedIntakeIds(new Set());
  };

  // Check if all visible items on page are selected
  const allOnPageSelected = pagedIntakes.length > 0 && pagedIntakes.every((i) => selectedIntakeIds.has(i.id));
  const someOnPageSelected = pagedIntakes.some((i) => selectedIntakeIds.has(i.id)) && !allOnPageSelected;
  
  // Check if all filtered items are selected
  const allFilteredSelected = filteredIntakes.length > 0 && filteredIntakes.every((i) => selectedIntakeIds.has(i.id));

  const totalAppointmentPages = Math.max(1, Math.ceil(appointments.length / appointmentPageSize));
  const pagedAppointments = appointments.slice((appointmentsPage - 1) * appointmentPageSize, appointmentsPage * appointmentPageSize);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <Header showAdminLink={true} />
      
      <main className="pt-[98px] md:pt-[105px] pb-20">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="font-heading font-bold text-3xl text-navy">
                Admin Dashboard
              </h1>
              <p className="text-slate-600 mt-1 text-center">Manage client intake submissions</p>
            </div>
            <div className="flex gap-3 mt-4 md:mt-0 flex-wrap">
              {/* Back to Dashboard button when filtered */}
              {(statusFilter !== "total" || currentView !== "dashboard") && (
                <Button
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    setStatusFilter("total");
                    setCurrentView("dashboard");
                    setIntakePage(1);
                  }}
                  variant="outline"
                  className="border-gold text-gold hover:bg-gold hover:text-navy"
                  data-testid="btn-back-dashboard"
                >
                  ← Back to Dashboard
                </Button>
              )}
              <Button
                onClick={fetchData}
                variant="outline"
                className="border-navy text-navy hover:bg-navy hover:text-white"
                data-testid="btn-refresh"
              >
                <RefreshCw className="mr-2" size={18} />
                Refresh
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                data-testid="btn-logout"
              >
                <LogOut className="mr-2" size={18} />
                Logout
              </Button>
            </div>
          </div>

          {/* Weekly Backup Reminder - Shows on Fridays */}
          {showBackupReminder && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-300 rounded-lg p-4 shadow-sm"
              data-testid="weekly-backup-reminder"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="bg-amber-100 rounded-full p-2">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                      📅 Weekly Backup Reminder
                      <span className="text-xs font-normal bg-amber-200 text-amber-700 px-2 py-0.5 rounded">Friday</span>
                    </h3>
                    <p className="text-amber-700 text-sm mt-1">
                      Export client list and documents. Confirm Drive backup is complete.
                    </p>
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-amber-500 text-amber-700 hover:bg-amber-100"
                        onClick={() => {
                          openExportPreview();
                          dismissBackupReminder();
                        }}
                        data-testid="export-csv-reminder"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Export CSV
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-amber-500 text-amber-700 hover:bg-amber-100"
                        onClick={() => {
                          exportAllDocuments();
                          dismissBackupReminder();
                        }}
                        data-testid="export-docs-reminder"
                      >
                        <FileCheck className="w-4 h-4 mr-1" />
                        Export All Docs
                      </Button>
                    </div>
                  </div>
                </div>
                <button
                  onClick={dismissBackupReminder}
                  className="text-amber-500 hover:text-amber-700 p-1"
                  data-testid="dismiss-backup-reminder"
                >
                  ✕
                </button>
              </div>
            </motion.div>
          )}

          {/* Main Content with Calendar Sidebar */}
          <div className="flex gap-6">
            {/* Left Column - Main Content */}
            <div className="flex-1">
          {/* Stats Cards - Always visible */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
            {statCards.map((stat, index) => (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Card
                  className={`border-0 shadow-card cursor-pointer ${statusFilter === stat.key ? 'ring-2 ring-gold' : ''}`}
                  data-testid={`stat-card-${index}`}
                  onClick={() => {
                    if (stat.link) {
                      navigate(stat.link);
                      return;
                    }
                    // For status-based cards, filter the client list
                    if (["Intake Received", "In Preparation", "Filed", "Completed", "total"].includes(stat.key)) {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      setStatusFilter(stat.key);
                      setCurrentView("intakes");
                      setIntakePage(1);
                      return;
                    }
                    // Appointments, Deleted, and Reviews show different views
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    setStatusFilter(stat.key);
                    if (stat.key === "Appointments") setCurrentView("appointments");
                    else if (stat.key === "Deleted") setCurrentView("deleted");
                    else if (stat.key === "Reviews") setCurrentView("reviews");
                    else if (stat.key === "PortalUsers") setCurrentView("portal-users");
                    else if (stat.key === "FeeTracker") setCurrentView("fee-tracker");
                    else if (stat.key === "BatchFolders") {
                      fetchPaymentBatches();
                      setCurrentView("batch-folders");
                    }
                    else setCurrentView("intakes");
                    setIntakePage(1);
                    setAppointmentsPage(1);
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="text-slate-500 text-xs md:text-sm whitespace-nowrap">{stat.title}</p>
                        <p className={`text-2xl font-bold ${stat.color} mt-1`}>{stat.value}</p>
                      </div>

                      <stat.icon className={`${stat.color} opacity-20`} size={28} />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Appointments - Only show when appointments tab is clicked */}
          {currentView === "appointments" && (
            <Card className="border-0 shadow-card" data-testid="appointments-table">
            <CardHeader className="border-b bg-slate-50/50">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-6 py-4">
                <div className="text-center w-full md:w-auto">
                  <CardTitle className="font-heading text-navy text-2xl font-bold">Appointments</CardTitle>
                </div>
                <div className="flex items-center justify-center md:justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={deleteSelectedAppointments}
                    className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white h-9"
                    data-testid="btn-delete-selected-appts"
                  >
                    Delete Selected
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {appointments.length === 0 ? (
                <div className="p-8 text-center text-slate-500 text-sm">No appointments scheduled</div>
              ) : (
                <div className="overflow-x-auto">


                  <Table className="data-table min-w-[980px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <div className="flex items-center justify-center">
                            <Checkbox
                              checked={selectedAppointmentIds.size === appointments.length && appointments.length > 0}
                              onCheckedChange={(checked) => toggleSelectAllAppointments(Boolean(checked))}
                              aria-label="Select all appointments"
                              data-testid="appt-select-all"
                            />
                          </div>
                        </TableHead>
                        <TableHead>Client Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Appointment Time (EST)</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Calendar</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pagedAppointments.map((appt) => (
                        <TableRow key={appt.id}>
                          <TableCell className="w-12">
                            <div className="flex items-center justify-center">
                              <Checkbox
                                checked={selectedAppointmentIds.has(appt.id)}
                                onCheckedChange={(checked) => toggleAppointmentSelection(appt.id, Boolean(checked))}
                                aria-label={`Select appointment ${appt.id}`}
                                data-testid={`appt-select-${appt.id}`}
                              />
                            </div>
                          </TableCell>
                          <TableCell>{appt.client_name}</TableCell>
                          <TableCell>{appt.client_email}</TableCell>
                          <TableCell>
                            {appt.start_time ? estDateTime(appt.start_time) : ""}
                          </TableCell>
                          <TableCell>{appt.client_phone || "-"}</TableCell>
                          <TableCell>
                            <select
                              className={`h-8 rounded-md border px-2 text-sm font-medium ${
                                appt.status === "Scheduled" ? "bg-blue-100 text-blue-700 border-blue-300" :
                                appt.status === "Rescheduled" ? "bg-blue-500 text-white border-blue-600" :
                                appt.status === "Cancelled" ? "bg-red-100 text-red-700 border-red-300" :
                                "bg-white text-slate-700 border-slate-200"
                              }`}
                              value={appt.status || "Scheduled"}
                              onChange={async (e) => {
                                const nextStatus = e.target.value;
                                if (nextStatus === "Reschedule") {
                                  openRescheduleModal(appt);
                                  return;
                                }
                                try {
                                  await axios.patch(
                                    `${API}/admin/appointments/${appt.id}`,
                                    { status: nextStatus },
                                    getAuthConfig()
                                  );
                                  toast.success("Appointment status updated");
                                  fetchData();
                                } catch {
                                  toast.error("Failed to update appointment status");
                                }
                              }}
                              data-testid={`appointment-status-${appt.id}`}
                            >
                              <option value="Scheduled">Scheduled</option>
                              <option value="Rescheduled">Rescheduled</option>
                              <option value="Reschedule">Reschedule</option>
                              <option value="Cancelled">Cancelled</option>
                            </select>
                          </TableCell>
                          <TableCell>
                            <a
                              href={`${BACKEND_URL}/api/appointments/${appt.id}/calendar.ics`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-navy hover:underline"
                            >
                              Add to Calendar
                            </a>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              
              {/* Appointments Pagination */}
              {appointments.length > appointmentPageSize && (
                <div className="flex items-center justify-between px-6 py-4 border-t">
                  <p className="text-sm text-slate-500">
                    Showing {(appointmentsPage - 1) * appointmentPageSize + 1} - {Math.min(appointmentsPage * appointmentPageSize, appointments.length)} of {appointments.length} appointments
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAppointmentsPage(p => Math.max(1, p - 1))}
                      disabled={appointmentsPage === 1}
                      data-testid="appointments-prev-page"
                    >
                      Previous
                    </Button>
                    <span className="flex items-center px-3 text-sm">
                      Page {appointmentsPage} of {totalAppointmentPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAppointmentsPage(p => Math.min(totalAppointmentPages, p + 1))}
                      disabled={appointmentsPage === totalAppointmentPages}
                      data-testid="appointments-next-page"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          )}


          {/* Client Submissions Table */}
          {(currentView === "dashboard" || currentView === "intakes") && (
            <Card className="border-0 shadow-card mt-8" data-testid="submissions-table">
            <CardHeader className="border-b bg-slate-50/50">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-6 py-4">
                <div className="text-center w-full md:w-auto">
                  <CardTitle className="font-heading text-navy text-2xl font-bold flex items-center gap-2 justify-center md:justify-start">
                    Client Submissions
                    {statusFilter !== "total" && (
                      <>
                        <span className="text-base font-normal bg-gold/20 text-gold px-2 py-1 rounded">
                          {statusFilter}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setStatusFilter("total")}
                          className="text-slate-500 hover:text-red-500 h-7 px-2"
                          data-testid="btn-clear-filter"
                        >
                          ✕ Clear
                        </Button>
                      </>
                    )}
                  </CardTitle>
                  {statusFilter !== "total" && (
                    <p className="text-slate-500 text-sm mt-1">
                      Showing {filteredIntakes.length} clients with &quot;{statusFilter}&quot; status
                    </p>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center md:justify-end gap-2">
                  <div className="relative w-full sm:w-80">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <Input
                      type="text"
                      placeholder="Search name, email, phone, status..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setIntakePage(1);
                      }}
                      className="h-9 pl-9"
                      data-testid="admin-search"
                    />
                  </div>

                  <select
                    className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setIntakePage(1);
                    }}
                    data-testid="filter-status"
                  >
                    <option value="total">All Statuses</option>
                    <option value="Intake Received">Intake Received</option>
                    <option value="In Preparation">In Preparation</option>
                    <option value="Filed">Filed</option>
                    <option value="Completed">Completed</option>
                    <option value="Master File – Active">Master File</option>
                    <option value="Duplicate – Do Not Use">Duplicate</option>
                    <option value="Archived – Superseded">Archived</option>
                    <option value="Merged – Archived">Merged</option>
                  </select>

                  <select
                    className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                    value={sortOption}
                    onChange={(e) => {
                      setSortOption(e.target.value);
                    }}
                    data-testid="sort-intakes"
                  >
                    <option value="newest">Newest</option>
                    <option value="oldest">Oldest</option>
                    <option value="alpha">A–Z</option>
                  </select>

                  {/* Clear selection button */}
                  {selectedIntakeIds.size > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearSelection}
                      className="text-slate-500 hover:text-slate-700 h-9"
                      data-testid="btn-clear-selection"
                    >
                      Clear
                    </Button>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openExportPreview}
                    disabled={exportLoading}
                    className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white h-9"
                    data-testid="btn-export-csv"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    {exportLoading ? "Exporting..." : "Export CSV"}
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportAllDocuments}
                    className="border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white h-9"
                    data-testid="btn-export-all-docs"
                  >
                    <FileCheck className="w-4 h-4 mr-1" />
                    Export All Docs
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={deleteSelectedIntakes}
                    disabled={selectedIntakeIds.size === 0}
                    className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white h-9"
                    data-testid="btn-delete-selected-intakes"
                  >
                    Delete Selected
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-12 text-center">



                  <RefreshCw className="w-8 h-8 text-slate-400 animate-spin mx-auto" />
                  <p className="text-slate-500 mt-3">Loading submissions...</p>
                </div>
              ) : intakes.length === 0 ? (
                <div className="p-12 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No submissions yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table className="data-table min-w-[1100px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <div className="flex items-center justify-center" title="Select all on this page">
                            <Checkbox
                              checked={allOnPageSelected}
                              indeterminate={someOnPageSelected}
                              onCheckedChange={(checked) => toggleSelectAllIntakes(Boolean(checked))}
                              aria-label="Select all clients on this page"
                              data-testid="intake-select-all"
                            />
                          </div>
                        </TableHead>
                        <TableHead>Client Name</TableHead>
                        <TableHead className="text-center">Email</TableHead>
                        <TableHead>Filing Status</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead>Referral</TableHead>
                        <TableHead>Referral Rewards</TableHead>
                        <TableHead>Submitted</TableHead>
                        <TableHead>Resend</TableHead>
                        <TableHead className="text-center">Email Sent</TableHead>
                        <TableHead>Fees ($)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pagedIntakes.map((intake) => (
                        <TableRow key={intake.id} data-testid={`row-${intake.id}`}>
                          <TableCell className="w-12">
                            <div className="flex items-center justify-center">
                              <Checkbox
                                checked={selectedIntakeIds.has(intake.id)}
                                onCheckedChange={(checked) => toggleIntakeSelection(intake.id, Boolean(checked))}
                                aria-label={`Select client ${intake.id}`}
                                data-testid={`intake-select-${intake.id}`}
                              />
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">
                            <button
                              type="button"
                              onClick={() => viewDetails(intake)}
                              className="text-navy hover:underline"
                              data-testid={`client-name-${intake.id}`}
                            >
                              {intake.contact.first_name} {intake.contact.last_name}
                            </button>
                          </TableCell>
                          <TableCell className="text-center">{intake.contact.email}</TableCell>

                          <TableCell className="capitalize">
                            {intake.tax_details.filing_status?.replace('_', ' ') || '-'}
                          </TableCell>
                          <TableCell className="text-center">
                            <Select
                              value={intake.status}
                              onValueChange={(value) => {
                                if (value === "In Preparation") {
                                  const ok = window.confirm("In Preparation is an internal status and does not send an email. Continue?");
                                  if (!ok) return;
                                }
                                if (value === "Filed") {
                                  const ok = window.confirm("This will send a 'Filed' confirmation email to the client. Continue?");
                                  if (!ok) return;
                                }
                                if (value === "Completed") {
                                  const ok = window.confirm("This will send a completion/thank-you email to the client. Continue?");
                                  if (!ok) return;
                                }
                                updateStatus(intake.id, value);
                              }}
                            >
                              <SelectTrigger className="w-36 h-8" data-testid={`status-select-${intake.id}`}>
                                <SelectValue>
                                  <StatusBadge status={intake.status} />
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Intake Received">Intake Received</SelectItem>
                                <SelectItem value="In Preparation">In Preparation</SelectItem>
                                <SelectItem value="Filed">Filed</SelectItem>
                                <SelectItem value="Completed">Completed</SelectItem>
                                <SelectItem value="Master File – Active" className="text-green-600">✅ Master File</SelectItem>
                                <SelectItem value="Duplicate – Do Not Use" className="text-red-600">⚠️ Duplicate</SelectItem>
                                <SelectItem value="Archived – Superseded" className="text-slate-600">📁 Archived</SelectItem>
                                <SelectItem value="Merged – Archived" className="text-purple-600">🔀 Merged</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            {intake.contact?.referral_name ? (
                              <span className="text-sm text-slate-700">{intake.contact.referral_name}</span>
                            ) : (
                              <span className="text-sm text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <select
                              className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm"
                              value={intake.referral_reward_status || "Pending"}
                              onChange={(e) => updateReferralReward(intake, e.target.value)}
                              disabled={!intake.contact?.referral_name}
                              data-testid={`referral-reward-${intake.id}`}
                            >
                              <option value="Pending">Pending</option>
                              <option value="Paid">Paid</option>
                              <option value="Not Eligible">Not Eligible</option>
                            </select>
                          </TableCell>
                          <TableCell className="text-slate-500 text-sm">
                            {formatDate(intake.created_at)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => resendIntakeEmail(intake.id)}
                              className="h-8"
                              data-testid={`btn-resend-${intake.id}`}
                            >
                              Resend
                            </Button>
                          </TableCell>
                          <TableCell className="text-center">
                            {(() => {
                              const deliveryStatus = intake.emails?.intake?.delivery_status;
                              if (deliveryStatus === "Delivered") {
                                return (
                                  <div className="flex items-center justify-center" title="Email Delivered">
                                    <MailCheck className="w-5 h-5 text-green-600" data-testid={`email-delivered-${intake.id}`} />
                                  </div>
                                );
                              } else if (deliveryStatus === "Not Delivered") {
                                return (
                                  <div className="flex items-center justify-center" title="Email Not Delivered">
                                    <MailX className="w-5 h-5 text-red-500" data-testid={`email-not-delivered-${intake.id}`} />
                                  </div>
                                );
                              } else {
                                return (
                                  <div className="flex items-center justify-center" title="Email Status Pending">
                                    <Mail className="w-5 h-5 text-slate-400" data-testid={`email-pending-${intake.id}`} />
                                  </div>
                                );
                              }
                            })()}
                          </TableCell>
                          <TableCell>
                            {editingFeeId === intake.id ? (
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={editingFeeValue}
                                  onChange={(e) => setEditingFeeValue(e.target.value)}
                                  className="w-20 h-8 text-sm"
                                  placeholder="0.00"
                                  data-testid={`fee-input-${intake.id}`}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") updateFee(intake.id, editingFeeValue);
                                    if (e.key === "Escape") cancelEditingFee();
                                  }}
                                  autoFocus
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => updateFee(intake.id, editingFeeValue)}
                                  className="h-8 px-2 text-green-600 hover:text-green-700"
                                  data-testid={`fee-save-${intake.id}`}
                                >
                                  ✓
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={cancelEditingFee}
                                  className="h-8 px-2 text-red-500 hover:text-red-600"
                                  data-testid={`fee-cancel-${intake.id}`}
                                >
                                  ✕
                                </Button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => startEditingFee(intake)}
                                className="text-sm text-navy hover:underline cursor-pointer min-w-[50px] text-left"
                                data-testid={`fee-display-${intake.id}`}
                              >
                                {intake.fee !== null && intake.fee !== undefined
                                  ? `$${Number(intake.fee).toFixed(2)}`
                                  : "—"}
                              </button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Totals Row */}
              {!loading && intakes.length > 0 && (
                <div className="border-t bg-slate-50 px-6 py-4">
                  <div className="flex justify-end gap-8 text-sm font-semibold">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-600">Total Referral Rewards:</span>
                      <span className="text-green-600" data-testid="total-referral-rewards">
                        ${totals.total_referral_rewards?.toFixed(2) || "0.00"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-600">Total Fees:</span>
                      <span className="text-navy" data-testid="total-fees">
                        ${totals.total_fees?.toFixed(2) || "0.00"}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Pagination Controls */}
              {!loading && totalIntakePages > 1 && (
                <div className="border-t bg-white px-6 py-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-500">
                      Showing {((intakePage - 1) * pageSize) + 1} - {Math.min(intakePage * pageSize, sortedIntakes.length)} of {sortedIntakes.length} clients
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIntakePage(1)}
                        disabled={intakePage === 1}
                        className="h-8 px-2"
                        data-testid="btn-first-page"
                      >
                        First
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIntakePage(p => Math.max(1, p - 1))}
                        disabled={intakePage === 1}
                        className="h-8 px-3"
                        data-testid="btn-prev-page"
                      >
                        ← Prev
                      </Button>
                      <span className="text-sm text-slate-600 px-2" data-testid="page-indicator">
                        Page {intakePage} of {totalIntakePages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIntakePage(p => Math.min(totalIntakePages, p + 1))}
                        disabled={intakePage === totalIntakePages}
                        className="h-8 px-3"
                        data-testid="btn-next-page"
                      >
                        Next →
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIntakePage(totalIntakePages)}
                        disabled={intakePage === totalIntakePages}
                        className="h-8 px-2"
                        data-testid="btn-last-page"
                      >
                        Last
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Reviews Management Section */}
          {currentView === "reviews" && (
            <Card className="border-0 shadow-card" data-testid="reviews-section">
              <CardHeader className="border-b bg-slate-50/50">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-6 py-4">
                  <div className="text-center w-full md:w-auto">
                    <CardTitle className="font-heading text-navy text-2xl font-bold flex items-center gap-2 justify-center md:justify-start">
                      <MessageSquare className="w-6 h-6" />
                      Client Reviews
                    </CardTitle>
                    <p className="text-slate-500 text-sm mt-1">Manage testimonials displayed on your website</p>
                  </div>
                  <Button
                    onClick={() => openReviewModal()}
                    className="bg-gold text-navy hover:bg-gold-hover font-semibold"
                    data-testid="btn-add-review"
                  >
                    <Plus className="mr-2" size={18} />
                    Add New Review
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {reviews.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="mx-auto text-slate-300" size={48} />
                    <p className="text-slate-500 mt-4">No reviews yet. Add your first testimonial!</p>
                    <Button
                      onClick={() => openReviewModal()}
                      className="mt-4 bg-navy text-white hover:bg-navy-dark"
                    >
                      Add First Review
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reviews.map((review) => (
                      <div 
                        key={review.id} 
                        className={`p-4 rounded-lg border ${review.is_active ? 'bg-white border-slate-200' : 'bg-red-50 border-red-200'}`}
                        data-testid={`review-item-${review.id}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="font-semibold text-navy">{review.name}</span>
                              <span className="text-slate-400">•</span>
                              <span className="text-slate-500 text-sm">{review.location}</span>
                              {!review.is_active && (
                                <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded-full">Deleted</span>
                              )}
                            </div>
                            <div className="flex gap-1 mb-2">
                              {[...Array(review.rating || 5)].map((_, i) => (
                                <Star key={i} className="text-gold fill-gold" size={16} />
                              ))}
                              {[...Array(5 - (review.rating || 5))].map((_, i) => (
                                <Star key={i} className="text-slate-200" size={16} />
                              ))}
                            </div>
                            <p className="text-slate-600 text-sm italic mb-2">"{review.review}"</p>
                            <div className="flex items-center gap-2">
                              <span className="text-xs bg-navy/10 text-navy px-2 py-1 rounded-full">{review.service}</span>
                              {review.is_featured && (
                                <span className="text-xs bg-gold/20 text-gold px-2 py-1 rounded-full flex items-center gap-1">
                                  <Eye size={12} /> Featured
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {review.is_active ? (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => toggleReviewFeatured(review.id)}
                                  className={`h-8 ${review.is_featured ? 'border-gold text-gold' : 'border-slate-300 text-slate-500'}`}
                                  title={review.is_featured ? "Remove from featured" : "Add to featured"}
                                  data-testid={`btn-toggle-featured-${review.id}`}
                                >
                                  {review.is_featured ? <EyeOff size={16} /> : <Eye size={16} />}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openReviewModal(review)}
                                  className="h-8 border-navy text-navy hover:bg-navy hover:text-white"
                                  data-testid={`btn-edit-review-${review.id}`}
                                >
                                  <Edit size={16} />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => deleteReview(review.id)}
                                  className="h-8 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                                  data-testid={`btn-delete-review-${review.id}`}
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => restoreReview(review.id)}
                                className="h-8 border-green-500 text-green-500 hover:bg-green-500 hover:text-white"
                                data-testid={`btn-restore-review-${review.id}`}
                              >
                                <RotateCcw size={16} className="mr-1" /> Restore
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Portal Users View */}
          {currentView === "portal-users" && (
            <Card className="border-0 shadow-card" data-testid="portal-users-section">
              <CardHeader className="border-b bg-slate-50/50">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-6 py-4">
                  <div className="text-center w-full md:w-auto">
                    <CardTitle className="font-heading text-navy text-2xl font-bold flex items-center gap-2 justify-center md:justify-start">
                      <Users className="w-6 h-6" />
                      Client Portal Users
                    </CardTitle>
                    <p className="text-slate-500 text-sm mt-1">Manage client portal accounts ({portalUsersCount} total)</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {loadingPortalUsers ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy mx-auto"></div>
                    <p className="text-slate-500 mt-4">Loading portal users...</p>
                  </div>
                ) : portalUsers.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="mx-auto text-slate-300" size={48} />
                    <p className="text-slate-500 mt-4">No portal users registered yet.</p>
                    <p className="text-slate-400 text-sm mt-2">Clients can create a portal account after submitting their intake form.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Name</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Email</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Phone</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Created</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-200">
                        {portalUsers.map((user) => (
                          <tr key={user.id} className="hover:bg-slate-50">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="font-medium text-navy">
                                {user.first_name || user.name || ''} {user.last_name || ''}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                              {user.email}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                              {user.phone || '-'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">
                              {user.created_at ? estDateTime(user.created_at) : '-'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-500 border-red-300 hover:bg-red-50"
                                onClick={() => deletePortalUser(user.id)}
                                data-testid={`btn-delete-portal-user-${user.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Fee Tracker Section */}
          {currentView === "fee-tracker" && (
            <Card className="border-0 shadow-card" data-testid="fee-tracker-section">
              <CardHeader className="border-b bg-slate-50/50">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-6 py-4">
                  <div className="text-center w-full md:w-auto">
                    <CardTitle className="font-heading text-navy text-2xl font-bold flex items-center gap-2 justify-center md:justify-start">
                      <DollarSign className="w-6 h-6" />
                      Fee & Referral Tracker
                    </CardTitle>
                    <p className="text-slate-500 text-sm mt-1">Track client fees and referral rewards</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <div className="bg-emerald-50 px-3 py-2 rounded-lg border border-emerald-200">
                      <span className="text-emerald-600 font-medium">Total Fees:</span>
                      <span className="text-emerald-700 font-bold ml-2">${feeSummary.total_fees.toLocaleString()}</span>
                    </div>
                    <div className="bg-green-100 px-3 py-2 rounded-lg border border-green-300">
                      <span className="text-green-700 font-medium">Refund Received:</span>
                      <span className="text-green-800 font-bold ml-2">${feeSummary.refund_received_fees.toLocaleString()}</span>
                      <span className="text-green-600 text-xs ml-1">({feeSummary.refund_received_count})</span>
                    </div>
                    <div className="bg-blue-50 px-3 py-2 rounded-lg border border-blue-200">
                      <span className="text-blue-600 font-medium">Total Referrals:</span>
                      <span className="text-blue-700 font-bold ml-2">${feeSummary.total_referrals.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {/* Batch Payment Section */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg mb-6 border border-green-200">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-green-800 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" />
                        Batch Mark as Paid
                      </h3>
                      <p className="text-green-600 text-sm mt-1">Select multiple clients below and mark them all as paid on a specific date</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {selectedFeeEntryIds.size > 0 && (
                        <span className="bg-green-200 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                          {selectedFeeEntryIds.size} selected
                        </span>
                      )}
                      <Popover open={batchPaymentOpen} onOpenChange={setBatchPaymentOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            className="bg-green-600 hover:bg-green-700 text-white"
                            disabled={selectedFeeEntryIds.size === 0}
                            data-testid="batch-payment-btn"
                          >
                            <CalendarDays className="w-4 h-4 mr-2" />
                            Set Payment Date
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="end">
                          <div className="p-3 border-b bg-green-50">
                            <p className="text-sm font-medium text-green-800">Select Payment Date</p>
                            <p className="text-xs text-green-600 mt-1">
                              {selectedFeeEntryIds.size} client(s) will be marked as paid
                            </p>
                          </div>
                          <Calendar
                            mode="single"
                            selected={batchPaymentDate}
                            onSelect={setBatchPaymentDate}
                            initialFocus
                          />
                          <div className="p-3 border-t bg-slate-50 flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => { setBatchPaymentOpen(false); setBatchPaymentDate(null); }}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              className="flex-1 bg-green-600 hover:bg-green-700"
                              onClick={processBatchPayment}
                              disabled={!batchPaymentDate || processingBatchPayment}
                            >
                              {processingBatchPayment ? (
                                <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                              ) : (
                                <CheckCircle className="w-4 h-4 mr-1" />
                              )}
                              Confirm
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                      {selectedFeeEntryIds.size > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearFeeEntrySelection}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Search Bar */}
                <div className="mb-4 flex items-center gap-4">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search by client or referral name..."
                      value={feeSearchQuery}
                      onChange={(e) => setFeeSearchQuery(e.target.value)}
                      className="pl-10"
                      data-testid="fee-search"
                    />
                    {feeSearchQuery && (
                      <button
                        onClick={() => setFeeSearchQuery("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {feeEntries.filter(e => !e.refund_received).length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={selectAllUnpaidEntries}
                      className="text-green-600 border-green-300 hover:bg-green-50"
                    >
                      Select All Unpaid ({feeEntries.filter(e => !e.refund_received).length})
                    </Button>
                  )}
                </div>

                {/* Add New Entry Form */}
                <div className="bg-slate-50 p-4 rounded-lg mb-6 border border-slate-200">
                  <h3 className="font-semibold text-navy mb-4 flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Add New Entry
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">Client Name *</label>
                      <Input
                        placeholder="Enter client name"
                        value={feeFormData.client_name}
                        onChange={(e) => setFeeFormData({ ...feeFormData, client_name: e.target.value })}
                        data-testid="fee-client-name"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">Fee Amount ($)</label>
                      <Input
                        type="number"
                        placeholder="0.00"
                        value={feeFormData.fee_amount}
                        onChange={(e) => setFeeFormData({ ...feeFormData, fee_amount: e.target.value })}
                        data-testid="fee-amount"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">Referral Name</label>
                      <Input
                        placeholder="Who referred them?"
                        value={feeFormData.referral_name}
                        onChange={(e) => setFeeFormData({ ...feeFormData, referral_name: e.target.value })}
                        data-testid="fee-referral-name"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">Referral Amount ($)</label>
                      <Input
                        type="number"
                        placeholder="0.00"
                        value={feeFormData.referral_amount}
                        onChange={(e) => setFeeFormData({ ...feeFormData, referral_amount: e.target.value })}
                        data-testid="fee-referral-amount"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button
                        className="w-full bg-navy hover:bg-navy-light"
                        onClick={addFeeEntry}
                        disabled={savingFeeEntry}
                        data-testid="add-fee-entry-btn"
                      >
                        {savingFeeEntry ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4 mr-2" />
                        )}
                        Add Entry
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Entries Table */}
                {(() => {
                  const filteredFeeEntries = feeEntries.filter(entry => {
                    if (!feeSearchQuery.trim()) return true;
                    const query = feeSearchQuery.toLowerCase();
                    return (
                      (entry.client_name || '').toLowerCase().includes(query) ||
                      (entry.referral_name || '').toLowerCase().includes(query)
                    );
                  });
                  
                  // Pagination calculations
                  const totalPages = Math.ceil(filteredFeeEntries.length / feePageSize);
                  const startIndex = (feeCurrentPage - 1) * feePageSize;
                  const paginatedEntries = filteredFeeEntries.slice(startIndex, startIndex + feePageSize);
                  
                  // Reset to page 1 if current page is out of bounds
                  if (feeCurrentPage > totalPages && totalPages > 0) {
                    setFeeCurrentPage(1);
                  }
                  
                  if (feeEntries.length === 0) {
                    return (
                      <div className="text-center py-12">
                        <DollarSign className="mx-auto text-slate-300" size={48} />
                        <p className="text-slate-500 mt-4">No fee entries yet.</p>
                        <p className="text-slate-400 text-sm mt-2">Add your first entry using the form above.</p>
                      </div>
                    );
                  }
                  
                  if (filteredFeeEntries.length === 0) {
                    return (
                      <div className="text-center py-12">
                        <Search className="mx-auto text-slate-300" size={48} />
                        <p className="text-slate-500 mt-4">No matching entries found.</p>
                        <p className="text-slate-400 text-sm mt-2">Try a different search term.</p>
                      </div>
                    );
                  }
                  
                  return (
                  <div className="overflow-x-auto">
                    {feeSearchQuery && (
                      <p className="text-sm text-slate-500 mb-2">
                        Showing {startIndex + 1}-{Math.min(startIndex + feePageSize, filteredFeeEntries.length)} of {filteredFeeEntries.length} entries
                      </p>
                    )}
                    <table className="min-w-full divide-y divide-slate-200">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider w-10">
                            <span className="sr-only">Select</span>
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Client Name</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Fee Amount</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Referral Name</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Referral Amount</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Refund Received</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Refund Date</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Date Added</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-200">
                        {paginatedEntries.map((entry) => (
                          <tr key={entry.id} className={`hover:bg-slate-50 ${entry.refund_received ? 'bg-green-50' : ''} ${selectedFeeEntryIds.has(entry.id) ? 'bg-blue-50 ring-1 ring-blue-200' : ''}`}>
                            {editingFeeEntry === entry.id ? (
                              <>
                                <td className="px-3 py-2 text-center">
                                  <span className="text-slate-300">-</span>
                                </td>
                                <td className="px-4 py-2">
                                  <Input
                                    value={editFeeFormData.client_name}
                                    onChange={(e) => setEditFeeFormData({ ...editFeeFormData, client_name: e.target.value })}
                                    className="h-8 text-sm"
                                    data-testid={`edit-client-name-${entry.id}`}
                                  />
                                </td>
                                <td className="px-4 py-2">
                                  <Input
                                    type="number"
                                    value={editFeeFormData.fee_amount}
                                    onChange={(e) => setEditFeeFormData({ ...editFeeFormData, fee_amount: e.target.value })}
                                    className="h-8 text-sm w-24"
                                    data-testid={`edit-fee-amount-${entry.id}`}
                                  />
                                </td>
                                <td className="px-4 py-2">
                                  <Input
                                    value={editFeeFormData.referral_name}
                                    onChange={(e) => setEditFeeFormData({ ...editFeeFormData, referral_name: e.target.value })}
                                    className="h-8 text-sm"
                                    data-testid={`edit-referral-name-${entry.id}`}
                                  />
                                </td>
                                <td className="px-4 py-2">
                                  <Input
                                    type="number"
                                    value={editFeeFormData.referral_amount}
                                    onChange={(e) => setEditFeeFormData({ ...editFeeFormData, referral_amount: e.target.value })}
                                    className="h-8 text-sm w-24"
                                    data-testid={`edit-referral-amount-${entry.id}`}
                                  />
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className="text-slate-400 text-xs">-</span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">
                                  {entry.refund_received_date ? new Date(entry.refund_received_date).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  }) : '-'}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">
                                  {entry.created_at ? new Date(entry.created_at).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  }) : '-'}
                                </td>
                                <td className="px-4 py-2">
                                  <div className="flex gap-1">
                                    <Button
                                      size="sm"
                                      className="bg-emerald-500 hover:bg-emerald-600 h-8 px-2"
                                      onClick={() => saveEditFeeEntry(entry.id)}
                                      data-testid={`save-fee-${entry.id}`}
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-8 px-2"
                                      onClick={cancelEditFeeEntry}
                                      data-testid={`cancel-edit-fee-${entry.id}`}
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="px-3 py-3 text-center">
                                  {!entry.refund_received && (
                                    <button
                                      onClick={() => toggleFeeEntrySelection(entry.id)}
                                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                                        selectedFeeEntryIds.has(entry.id)
                                          ? 'bg-blue-500 border-blue-500 text-white'
                                          : 'border-slate-300 hover:border-blue-400'
                                      }`}
                                      data-testid={`select-fee-${entry.id}`}
                                    >
                                      {selectedFeeEntryIds.has(entry.id) && <CheckCircle className="w-3 h-3" />}
                                    </button>
                                  )}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span className={`font-medium ${entry.refund_received ? 'text-green-700' : 'text-navy'}`}>{entry.client_name}</span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span className="text-emerald-600 font-semibold">
                                    ${(entry.fee_amount || 0).toLocaleString()}
                                  </span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                                  {entry.referral_name || '-'}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span className="text-blue-600 font-semibold">
                                    ${(entry.referral_amount || 0).toLocaleString()}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <button
                                    onClick={() => toggleRefundStatus(entry.id)}
                                    className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                                      entry.refund_received 
                                        ? 'bg-green-500 border-green-500 text-white' 
                                        : 'border-slate-300 hover:border-green-400'
                                    }`}
                                    data-testid={`refund-checkbox-${entry.id}`}
                                    title={entry.refund_received ? "Mark as not received" : "Mark as received"}
                                  >
                                    {entry.refund_received && <CheckCircle className="w-4 h-4" />}
                                  </button>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <Popover open={refundDatePickerOpen === entry.id} onOpenChange={(open) => setRefundDatePickerOpen(open ? entry.id : null)}>
                                    <PopoverTrigger asChild>
                                      <button
                                        className={`text-sm font-medium px-2 py-1 rounded border transition-all hover:bg-slate-100 ${
                                          entry.refund_received_date 
                                            ? 'text-green-600 border-green-300 bg-green-50' 
                                            : 'text-slate-400 border-slate-200 hover:border-slate-300'
                                        }`}
                                        data-testid={`refund-date-btn-${entry.id}`}
                                      >
                                        {entry.refund_received_date ? new Date(entry.refund_received_date).toLocaleDateString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric'
                                        }) : 'Set Date'}
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                      <div className="p-2 border-b bg-slate-50">
                                        <p className="text-sm font-medium text-navy">Select Refund Date</p>
                                      </div>
                                      <Calendar
                                        mode="single"
                                        selected={entry.refund_received_date ? new Date(entry.refund_received_date) : undefined}
                                        onSelect={(date) => updateRefundDate(entry.id, date)}
                                        initialFocus
                                      />
                                      {entry.refund_received_date && (
                                        <div className="p-2 border-t">
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="w-full text-red-500 border-red-300 hover:bg-red-50"
                                            onClick={() => clearRefundDate(entry.id)}
                                          >
                                            Clear Date
                                          </Button>
                                        </div>
                                      )}
                                    </PopoverContent>
                                  </Popover>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">
                                  {entry.created_at ? new Date(entry.created_at).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  }) : '-'}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="flex gap-1">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-blue-500 border-blue-300 hover:bg-blue-50"
                                      onClick={() => startEditFeeEntry(entry)}
                                      data-testid={`btn-edit-fee-${entry.id}`}
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-red-500 border-red-300 hover:bg-red-50"
                                      onClick={() => deleteFeeEntry(entry.id)}
                                      data-testid={`btn-delete-fee-${entry.id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    
                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t">
                        <p className="text-sm text-slate-500">
                          Page {feeCurrentPage} of {totalPages}
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setFeeCurrentPage(1)}
                            disabled={feeCurrentPage === 1}
                            className="px-2"
                          >
                            First
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setFeeCurrentPage(feeCurrentPage - 1)}
                            disabled={feeCurrentPage === 1}
                            className="px-3"
                          >
                            Previous
                          </Button>
                          
                          {/* Page Numbers */}
                          <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                              let pageNum;
                              if (totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (feeCurrentPage <= 3) {
                                pageNum = i + 1;
                              } else if (feeCurrentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = feeCurrentPage - 2 + i;
                              }
                              return (
                                <Button
                                  key={pageNum}
                                  variant={feeCurrentPage === pageNum ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => setFeeCurrentPage(pageNum)}
                                  className={`w-8 h-8 p-0 ${feeCurrentPage === pageNum ? 'bg-navy text-white' : ''}`}
                                >
                                  {pageNum}
                                </Button>
                              );
                            })}
                          </div>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setFeeCurrentPage(feeCurrentPage + 1)}
                            disabled={feeCurrentPage === totalPages}
                            className="px-3"
                          >
                            Next
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setFeeCurrentPage(totalPages)}
                            disabled={feeCurrentPage === totalPages}
                            className="px-2"
                          >
                            Last
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Batch Folders View */}
          {currentView === "batch-folders" && (
            <Card className="border-0 shadow-card" data-testid="batch-folders-section">
              <CardHeader className="border-b bg-slate-50/50">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-6 py-4">
                  <div className="text-center w-full md:w-auto">
                    <CardTitle className="font-heading text-navy text-2xl font-bold flex items-center gap-2 justify-center md:justify-start">
                      <FolderOpen className="w-6 h-6" />
                      Batch Folders
                    </CardTitle>
                    <p className="text-slate-500 text-sm mt-1">Track payment batches of clients who have been paid</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <div className="bg-indigo-50 px-3 py-2 rounded-lg border border-indigo-200">
                      <span className="text-indigo-600 font-medium">Total Batches:</span>
                      <span className="text-indigo-700 font-bold ml-2">{paymentBatches.length}</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentView("fee-tracker")}
                      className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                    >
                      <DollarSign className="w-4 h-4 mr-2" />
                      Go to Fee Tracker
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {paymentBatches.length === 0 ? (
                  <div className="text-center py-12">
                    <FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-600">No Payment Batches Yet</h3>
                    <p className="text-slate-400 mt-2">
                      Go to Fee Tracker and select clients to create your first batch
                    </p>
                    <Button
                      className="mt-4 bg-indigo-600 hover:bg-indigo-700"
                      onClick={() => setCurrentView("fee-tracker")}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create First Batch
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-0">
                    {paymentBatches.map((batch, index) => (
                      <div key={batch.id}>
                        {/* Batch Section */}
                        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                                <span className="text-indigo-700 font-bold text-lg">{batch.batch_number}</span>
                              </div>
                              <div>
                                <h3 className="text-lg font-bold text-navy">
                                  {batch.batch_number === 1 ? '1st' : batch.batch_number === 2 ? '2nd' : batch.batch_number === 3 ? '3rd' : `${batch.batch_number}th`} Batch
                                </h3>
                                <p className="text-slate-500 text-sm">
                                  {batch.entries?.length || 0} client{(batch.entries?.length || 0) !== 1 ? 's' : ''} • Total: ${(batch.total_fees || 0).toLocaleString()}
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-3">
                              {/* Payment Date - Editable */}
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-slate-500">Payment Date:</span>
                                {editingBatchId === batch.id ? (
                                  <div className="flex items-center gap-2">
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <Button variant="outline" size="sm" className="w-[140px]">
                                          <CalendarDays className="w-4 h-4 mr-2" />
                                          {editingBatchDate ? editingBatchDate.toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric'
                                          }) : 'Select Date'}
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-auto p-0" align="end">
                                        <Calendar
                                          mode="single"
                                          selected={editingBatchDate}
                                          onSelect={setEditingBatchDate}
                                          initialFocus
                                        />
                                      </PopoverContent>
                                    </Popover>
                                    <Button
                                      size="sm"
                                      className="bg-green-600 hover:bg-green-700"
                                      onClick={() => updateBatchDate(batch.id)}
                                      disabled={!editingBatchDate}
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => { setEditingBatchId(null); setEditingBatchDate(null); }}
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => {
                                      setEditingBatchId(batch.id);
                                      setEditingBatchDate(batch.payment_date ? new Date(batch.payment_date) : null);
                                    }}
                                    className="text-sm font-medium px-3 py-1.5 rounded border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors flex items-center gap-2"
                                    data-testid={`edit-batch-date-${batch.id}`}
                                  >
                                    <CalendarDays className="w-4 h-4" />
                                    {batch.payment_date ? new Date(batch.payment_date).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    }) : 'No Date'}
                                    <Edit className="w-3 h-3 opacity-60" />
                                  </button>
                                )}
                              </div>
                              
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-500 border-red-300 hover:bg-red-50"
                                onClick={() => deleteBatch(batch.id, batch.batch_number)}
                                data-testid={`delete-batch-${batch.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          
                          {/* Clients in Batch */}
                          {batch.entries && batch.entries.length > 0 && (
                            <div className="bg-slate-50 rounded-lg p-3 mt-3">
                              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Clients in this batch:</p>
                              <div className="flex flex-wrap gap-2">
                                {batch.entries.map((entry) => (
                                  <span
                                    key={entry.id}
                                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-white border border-slate-200 text-slate-700"
                                  >
                                    {entry.client_name}
                                    <span className="ml-2 text-emerald-600 font-medium">${(entry.fee_amount || 0).toLocaleString()}</span>
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Line Separator between batches */}
                        {index < paymentBatches.length - 1 && (
                          <div className="flex items-center py-4">
                            <div className="flex-1 border-t-2 border-dashed border-slate-300"></div>
                            <span className="px-4 text-slate-400 text-sm font-medium">next batch</span>
                            <div className="flex-1 border-t-2 border-dashed border-slate-300"></div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}


            </div>

            {/* Right Column - Calendar Sidebar (Desktop Only) */}
            <div className="hidden lg:block w-[320px] flex-shrink-0">
              <Card className="sticky top-32 border-0 shadow-card">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-navy text-lg flex items-center gap-2">
                    <CalendarDays className="w-5 h-5" />
                    Calendar
                  </CardTitle>
                  <p className="text-xs text-slate-500">Click a date to schedule appointment</p>
                </CardHeader>
                <CardContent className="pt-0">
                  <Calendar
                    mode="single"
                    selected={calendarSelectedDate}
                    onSelect={(date) => {
                      if (date) {
                        setCalendarSelectedDate(date);
                        setNewApptDate(date);
                        setNewApptModalOpen(true);
                      }
                    }}
                    className="rounded-md border w-full"
                    disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                    modifiers={{
                      hasAppointment: appointments.map(appt => {
                        if (appt.start_time) {
                          return new Date(appt.start_time);
                        }
                        return null;
                      }).filter(Boolean)
                    }}
                    modifiersStyles={{
                      hasAppointment: { 
                        backgroundColor: '#C9A227', 
                        color: '#121417',
                        fontWeight: 'bold',
                        borderRadius: '50%'
                      }
                    }}
                  />
                  
                  {/* Upcoming Appointments Summary */}
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="text-sm font-semibold text-navy mb-2">Upcoming Appointments</h4>
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {appointments
                        .filter(appt => (appt.status === 'Scheduled' || appt.status === 'Rescheduled') && appt.start_time && new Date(appt.start_time) >= new Date())
                        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
                        .slice(0, 5)
                        .map(appt => (
                          <div key={appt.id} className="p-2 bg-slate-50 rounded text-xs group hover:bg-slate-100">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-navy">{appt.client_name}</p>
                                <p className="text-slate-500">{estDateTime(appt.start_time)}</p>
                                {appt.notes && (
                                  <p className="text-slate-400 text-xs mt-1 italic truncate max-w-[150px]">{appt.notes}</p>
                                )}
                              </div>
                              <button
                                onClick={() => generateIcsFile(appt)}
                                className="text-navy hover:text-gold opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Add to Calendar"
                              >
                                <Download size={14} />
                              </button>
                            </div>
                          </div>
                        ))
                      }
                      {appointments.filter(appt => (appt.status === 'Scheduled' || appt.status === 'Rescheduled') && appt.start_time && new Date(appt.start_time) >= new Date()).length === 0 && (
                        <p className="text-sm text-slate-400">No upcoming appointments</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      {/* Details Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="details-dialog">
          <DialogHeader>
            <div className="flex items-start justify-between gap-4">
              <div>
                <DialogTitle className="font-heading text-navy text-xl">
                  Client Details
                </DialogTitle>
                <p className="text-slate-500 text-sm mt-1">
                  {selectedIntake?.contact?.first_name} {selectedIntake?.contact?.last_name}
                </p>
 
              </div>
            </div>
          </DialogHeader>
          
          {selectedIntake && (
            <div className="space-y-5 mt-4">
              {/* DUPLICATE WARNING BANNER */}
              {duplicateInfo?.has_duplicates && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-4" data-testid="duplicate-warning-banner">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-amber-800 text-base">⚠️ Possible Duplicate Client Detected</h4>
                      <p className="text-amber-700 text-sm mt-1">Review before proceeding. Golden Rule: One client. One intake. One return.</p>
                      <div className="mt-3 space-y-2">
                        {duplicateInfo.by_email?.length > 0 && (
                          <div className="text-sm">
                            <span className="font-medium text-amber-800">Same Email:</span>
                            <ul className="ml-4 mt-1 space-y-1">
                              {duplicateInfo.by_email.map((dup) => (
                                <li key={dup.id} className="text-amber-700">
                                  {dup.contact?.first_name} {dup.contact?.last_name} - {dup.contact?.email} 
                                  <span className="text-xs ml-2 px-1.5 py-0.5 bg-amber-200 rounded">{dup.status}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {duplicateInfo.by_name?.length > 0 && (
                          <div className="text-sm">
                            <span className="font-medium text-amber-800">Same Name:</span>
                            <ul className="ml-4 mt-1 space-y-1">
                              {duplicateInfo.by_name.map((dup) => (
                                <li key={dup.id} className="text-amber-700">
                                  {dup.contact?.first_name} {dup.contact?.last_name} - {dup.contact?.email}
                                  <span className="text-xs ml-2 px-1.5 py-0.5 bg-amber-200 rounded">{dup.status}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {duplicateInfo.by_phone?.length > 0 && (
                          <div className="text-sm">
                            <span className="font-medium text-amber-800">Same Phone:</span>
                            <ul className="ml-4 mt-1 space-y-1">
                              {duplicateInfo.by_phone.map((dup) => (
                                <li key={dup.id} className="text-amber-700">
                                  {dup.contact?.first_name} {dup.contact?.last_name} - {dup.contact?.email}
                                  <span className="text-xs ml-2 px-1.5 py-0.5 bg-amber-200 rounded">{dup.status}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                      
                      {/* Merge Actions */}
                      <div className="mt-4 pt-3 border-t border-amber-200">
                        <p className="text-amber-800 text-sm font-medium mb-2">🔀 Merge Options:</p>
                        <div className="flex flex-wrap gap-2">
                          {[...duplicateInfo.by_email, ...duplicateInfo.by_name, ...duplicateInfo.by_phone].map((dup) => (
                            <button
                              key={dup.id}
                              onClick={() => previewMerge(dup.id)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-purple-100 text-purple-700 rounded text-xs hover:bg-purple-200 transition-colors"
                              data-testid={`merge-btn-${dup.id}`}
                            >
                              <GitMerge size={14} />
                              Merge {dup.contact?.first_name} into this
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* LOCKED INTAKE BANNER */}
              {(selectedIntake.is_locked || selectedIntake.status === "Filed" || selectedIntake.status === "Completed") && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid="locked-banner">
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-blue-600" />
                    <span className="text-blue-700 text-sm font-medium">
                      🔒 This intake is locked ({selectedIntake.status}). Changes require admin unlock.
                    </span>
                  </div>
                </div>
              )}
              
              {/* CLIENT INFORMATION */}
              <div className="bg-slate-50 rounded-lg p-5">
                <div className="flex items-center justify-between mb-3 border-b pb-2">
                  <h3 className="font-semibold text-navy text-lg">👤 Client Information</h3>
                  {!editingContact ? (
                    <button
                      onClick={startEditingContact}
                      className="text-sm text-navy hover:text-gold flex items-center gap-1"
                      data-testid="edit-contact-btn"
                    >
                      <Edit size={14} /> Edit Contact
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingContact(false)}
                        className="text-sm text-slate-500 hover:text-slate-700 px-2 py-1"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveContact}
                        disabled={savingContact}
                        className="text-sm bg-navy text-white px-3 py-1 rounded hover:bg-navy-light disabled:opacity-50"
                      >
                        {savingContact ? "Saving..." : "Save"}
                      </button>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-500">First Name:</span>
                    <p className="font-medium">{selectedIntake?.contact?.first_name || '-'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Last Name:</span>
                    <p className="font-medium">{selectedIntake?.contact?.last_name || '-'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">SSN:</span>
                    <p className="font-medium">{selectedIntake?.contact?.ssn || '-'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Date of Birth:</span>
                    <p className="font-medium">{selectedIntake?.contact?.date_of_birth || '-'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Occupation:</span>
                    <p className="font-medium">{selectedIntake?.tax_details?.occupation || '-'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Status Type:</span>
                    <p className="font-medium capitalize">
                      {selectedIntake?.contact?.status_type === 'other'
                        ? `Other: ${selectedIntake?.contact?.status_type_other || ''}`
                        : (selectedIntake?.contact?.status_type?.replace(/_/g, ' ') || '-')}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-500">Phone Number:</span>
                    {editingContact ? (
                      <Input
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        className="mt-1 h-8"
                        placeholder="Phone number"
                      />
                    ) : (
                      <p className="font-medium">{selectedIntake?.contact?.phone || '-'}</p>
                    )}
                  </div>
                  <div>
                    <span className="text-slate-500">Phone Carrier:</span>
                    <p className="font-medium">{selectedIntake?.contact?.phone_carrier || '-'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Email:</span>
                    {editingContact ? (
                      <Input
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        className="mt-1 h-8"
                        placeholder="Email address"
                      />
                    ) : (
                      <p className="font-medium">{selectedIntake?.contact?.email || '-'}</p>
                    )}
                  </div>
                  <div>
                    <span className="text-slate-500">Gender:</span>
                    <p className="font-medium">{selectedIntake?.contact?.gender || '-'}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-slate-500">Address:</span>
                    <p className="font-medium">
                      {selectedIntake?.contact?.address}, {selectedIntake?.contact?.city}, {selectedIntake?.contact?.state} {selectedIntake?.contact?.zip_code}
                    </p>
                  </div>
                  {selectedIntake?.contact?.referral_name && (
                    <div className="col-span-2">
                      <span className="text-slate-500">Referred By:</span>
                      <p className="font-medium">{selectedIntake.contact.referral_name}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* SERVICES REQUESTED */}
              <div className="bg-amber-50 rounded-lg p-5">
                <h3 className="font-semibold text-navy mb-3 text-lg border-b border-amber-200 pb-2">📋 Services Requested</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedIntake.tax_details.services_needed?.includes('individual_tax_prep') && (
                    <span className="px-3 py-1 bg-navy text-white rounded-full text-sm font-medium">Tax Services</span>
                  )}
                  {selectedIntake.tax_details.services_needed?.includes('credit_repair') && (
                    <span className="px-3 py-1 bg-red-500 text-white rounded-full text-sm font-medium">Credit Repair</span>
                  )}
                  {selectedIntake.tax_details.services_needed?.includes('credit_education') && (
                    <span className="px-3 py-1 bg-orange-500 text-white rounded-full text-sm font-medium">Credit Education</span>
                  )}
                  {selectedIntake.tax_details.services_needed?.includes('small_business') && (
                    <span className="px-3 py-1 bg-green-600 text-white rounded-full text-sm font-medium">Small Business</span>
                  )}
                  {selectedIntake.tax_details.services_needed?.includes('1099') && (
                    <span className="px-3 py-1 bg-purple-600 text-white rounded-full text-sm font-medium">1099</span>
                  )}
                  {selectedIntake.tax_details.services_needed?.includes('funding') && (
                    <span className="px-3 py-1 bg-blue-600 text-white rounded-full text-sm font-medium">Funding</span>
                  )}
                  {(!selectedIntake.tax_details.services_needed || selectedIntake.tax_details.services_needed.length === 0) && (
                    <span className="text-slate-500 text-sm">No services selected</span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm mt-3 pt-3 border-t border-amber-200">
                  <div>
                    <span className="text-slate-500">Filing Status:</span>
                    <p className="font-medium capitalize">{selectedIntake.tax_details.filing_status?.replace(/_/g, ' ') || '-'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Income Type:</span>
                    <p className="font-medium uppercase">{selectedIntake.tax_details.income_type?.join(', ') || '-'}</p>
                  </div>
                </div>
                {Array.isArray(selectedIntake.tax_details.dependents) && selectedIntake.tax_details.dependents.length > 0 && selectedIntake.tax_details.dependents[0]?.name && (
                  <div className="mt-3 pt-3 border-t border-amber-200">
                    <span className="text-slate-500 text-sm">Dependents:</span>
                    <div className="mt-1">
                      {selectedIntake.tax_details.dependents.map((dep, idx) => (
                        dep?.name && (
                          <p key={idx} className="text-sm font-medium">
                            {dep.name} {dep.relationship && `(${dep.relationship})`} {dep.dob && `- DOB: ${dep.dob}`} {dep.ssn && `- SSN: ${dep.ssn}`}
                          </p>
                        )
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* SPOUSE INFORMATION - Only show if Married Filing Jointly */}
              {selectedIntake.tax_details.filing_status === 'married_filing_jointly' && (
                <div className="bg-blue-50 rounded-lg p-5">
                  <h3 className="font-semibold text-navy mb-3 text-lg border-b border-blue-200 pb-2">👫 Spouse Information</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-slate-500">First Name:</span>
                      <p className="font-medium">{selectedIntake.tax_details.spouse_first_name || '-'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Last Name:</span>
                      <p className="font-medium">{selectedIntake.tax_details.spouse_last_name || '-'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">SSN:</span>
                      <p className="font-medium">{selectedIntake.tax_details.spouse_ssn || '-'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Date of Birth:</span>
                      <p className="font-medium">{selectedIntake.tax_details.spouse_dob || '-'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Occupation:</span>
                      <p className="font-medium">{selectedIntake.tax_details.spouse_occupation || '-'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Status Type:</span>
                      <p className="font-medium capitalize">{selectedIntake.tax_details.spouse_status_type?.replace(/_/g, ' ') || '-'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Spouse Phone Number:</span>
                      {editingContact ? (
                        <Input
                          value={contactForm.spouse_phone}
                          onChange={(e) => setContactForm({ ...contactForm, spouse_phone: e.target.value })}
                          className="mt-1 h-8"
                          placeholder="Spouse phone"
                        />
                      ) : (
                        <p className="font-medium">{selectedIntake.tax_details.spouse_phone || '-'}</p>
                      )}
                    </div>
                    <div>
                      <span className="text-slate-500">Spouse Phone Carrier:</span>
                      <p className="font-medium">{selectedIntake.tax_details.spouse_phone_carrier || '-'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Spouse Email:</span>
                      {editingContact ? (
                        <Input
                          value={contactForm.spouse_email}
                          onChange={(e) => setContactForm({ ...contactForm, spouse_email: e.target.value })}
                          className="mt-1 h-8"
                          placeholder="Spouse email"
                        />
                      ) : (
                        <p className="font-medium">{selectedIntake.tax_details.spouse_email || '-'}</p>
                      )}
                    </div>
                    <div>
                      <span className="text-slate-500">Gender:</span>
                      <p className="font-medium">{selectedIntake.tax_details.spouse_gender || '-'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* BANK INFORMATION */}
              <div className="bg-green-50 rounded-lg p-5">
                <h3 className="font-semibold text-navy mb-3 text-lg border-b border-green-200 pb-2">🏦 Bank Information (Direct Deposit)</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-500">Bank Name:</span>
                    <p className="font-medium">{selectedIntake.documents?.direct_deposit?.bank_name || '-'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Account Type:</span>
                    <p className="font-medium capitalize">{selectedIntake.documents?.direct_deposit?.account_type || '-'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Routing Number:</span>
                    <p className="font-medium">{selectedIntake.documents?.direct_deposit?.routing_number || '-'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Account Number:</span>
                    <p className="font-medium">{selectedIntake.documents?.direct_deposit?.account_number || '-'}</p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-navy">Documents & Notes</h3>
                  <button
                    onClick={() => exportClientDocuments(
                      selectedIntake.id,
                      `${selectedIntake.contact?.last_name}_${selectedIntake.contact?.first_name}`
                    )}
                    className="flex items-center gap-1 px-3 py-1.5 bg-purple-100 text-purple-700 rounded text-xs hover:bg-purple-200 transition-colors"
                    data-testid="btn-export-client-docs"
                  >
                    <Download size={14} />
                    Export Docs ZIP
                  </button>
                </div>
                <div className="text-sm space-y-3">
                  <div>
                    <span className="text-slate-500">Uploaded Documents:</span>
                    {intakeDocuments.length > 0 ? (
                      <ul className="mt-2 space-y-2">
                        {intakeDocuments.map((doc) => {
                          const typeMapping = {
                            state_id: 'State ID',
                            ssn_card: 'SSN Card',
                            dependent_ssn: 'Dependent SSN Card',
                            dependent_birth_cert: 'Dependent Birth Certificate',
                            w2_1099: 'W-2 / 1099',
                            deduction_receipts: 'Deduction Receipts',
                            school_form: 'School Form',
                            health_insurance: 'Health Insurance',
                          };
                          const docTypeLabel = typeMapping[doc.doc_type] || doc.doc_type || '';
                          const isAvailable = doc.is_available !== false;
                          
                          return (
                            <li key={doc.id} className={`flex items-center justify-between gap-2 p-2 rounded border ${isAvailable ? 'bg-white' : 'bg-amber-50 border-amber-200'}`}>
                              <div className="flex-1 min-w-0">
                                <span className="font-medium text-navy block">
                                  {docTypeLabel && <span className="text-gold">{docTypeLabel}: </span>}
                                  <span className="truncate">{doc.filename}</span>
                                </span>
                                <span className="text-xs text-slate-400">
                                  {doc.content_type} • {doc.size ? `${Math.round(doc.size / 1024)}KB` : ''}
                                </span>
                                {!isAvailable && (
                                  <span className="text-xs text-amber-600 block mt-1">
                                    ⚠️ File unavailable - client needs to re-upload
                                  </span>
                                )}
                              </div>
                              {isAvailable ? (
                                <div className="flex gap-1">
                                  <button
                                    onClick={async () => {
                                      try {
                                        const response = await axios.get(
                                          `${API}/admin/documents/${doc.id}/download`,
                                          {
                                            ...getAuthConfig(),
                                            responseType: 'blob'
                                          }
                                        );
                                        
                                        // Open in new tab for viewing
                                        const url = window.URL.createObjectURL(new Blob([response.data], { type: doc.content_type }));
                                        window.open(url, '_blank');
                                      } catch (error) {
                                        console.error("View error:", error);
                                        toast.error("Failed to view document");
                                      }
                                    }}
                                    className="flex items-center gap-1 px-2 py-1.5 bg-slate-100 text-slate-700 rounded text-xs hover:bg-slate-200 transition-colors"
                                    data-testid={`view-doc-${doc.id}`}
                                  >
                                    <Eye size={14} />
                                    View
                                  </button>
                                  <button
                                    onClick={async () => {
                                      try {
                                        const response = await axios.get(
                                          `${API}/admin/documents/${doc.id}/download`,
                                          {
                                            ...getAuthConfig(),
                                            responseType: 'blob'
                                          }
                                        );
                                        
                                        const url = window.URL.createObjectURL(new Blob([response.data]));
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.setAttribute('download', doc.filename || 'document');
                                        document.body.appendChild(link);
                                        link.click();
                                        link.remove();
                                        window.URL.revokeObjectURL(url);
                                        toast.success("Document downloaded!");
                                      } catch (error) {
                                        console.error("Download error:", error);
                                        toast.error(error.response?.data?.detail || "Failed to download document");
                                      }
                                    }}
                                    className="flex items-center gap-1 px-2 py-1.5 bg-navy text-white rounded text-xs hover:bg-navy-dark transition-colors"
                                    data-testid={`download-doc-${doc.id}`}
                                  >
                                    <Download size={14} />
                                    Download
                                  </button>
                                  <button
                                    onClick={() => deleteDocument(doc.id)}
                                    className="flex items-center gap-1 px-2 py-1.5 bg-red-100 text-red-600 rounded text-xs hover:bg-red-200 transition-colors"
                                    data-testid={`delete-doc-${doc.id}`}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex gap-1">
                                  <span className="flex items-center gap-1 px-3 py-1.5 bg-slate-200 text-slate-500 rounded text-xs cursor-not-allowed">
                                    <Download size={14} />
                                    Unavailable
                                  </span>
                                  <button
                                    onClick={() => deleteDocument(doc.id)}
                                    className="flex items-center gap-1 px-2 py-1.5 bg-red-100 text-red-600 rounded text-xs hover:bg-red-200 transition-colors"
                                    data-testid={`delete-unavail-doc-${doc.id}`}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    ) : selectedIntake.documents?.document_names?.length > 0 ? (
                      <ul className="mt-2 space-y-1">
                        {selectedIntake.documents.document_names.map((name, index) => (
                          <li key={index} className="flex items-center gap-2 p-2 bg-white rounded border">
                            <span className="font-medium text-slate-700 text-sm">
                              {(() => {
                                const label = String(name || '');
                                const prefix = label.split(':')[0]?.trim();
                                const mapping = {
                                  state_id: 'State ID',
                                  ssn_card: 'SSN Card',
                                  dependent_ssn: 'Dependent SSN Card',
                                  dependent_birth_cert: 'Dependent Birth Certificate',
                                  w2_1099: 'W-2 / 1099',
                                  deduction_receipts: 'Deduction Receipts',
                                  school_form: 'School Form',
                                  health_insurance: 'Health Insurance',
                                };
                                const filename = label.includes(':') ? label.split(':').slice(1).join(':').trim() : label;
                                return mapping[prefix] ? `${mapping[prefix]}: ${filename}` : label;
                              })()}
                            </span>
                            <span className="text-xs text-amber-600 ml-auto">(No download available)</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="font-medium">No documents uploaded</p>
                    )}
                    
                    {/* Admin Upload Document Section */}
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <p className="text-slate-600 font-medium text-sm mb-2">Upload Document for Client</p>
                      <div className="flex flex-col gap-2">
                        <select
                          id="admin-upload-doc-type"
                          className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 text-sm"
                          defaultValue=""
                        >
                          <option value="">Select document type...</option>
                          <option value="state_id">State ID</option>
                          <option value="ssn_card">SSN Card</option>
                          <option value="w2_1099">W-2 / 1099</option>
                          <option value="dependent_ssn">Dependent SSN Card</option>
                          <option value="dependent_birth_cert">Dependent Birth Certificate</option>
                          <option value="deduction_receipts">Deduction Receipts</option>
                          <option value="health_insurance">Health Insurance</option>
                          <option value="other">Other</option>
                        </select>
                        <div className="flex gap-2">
                          <input
                            type="file"
                            id="admin-upload-file"
                            className="flex-1 text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-navy file:text-white hover:file:bg-navy-dark"
                          />
                          <Button
                            onClick={() => {
                              const fileInput = document.getElementById('admin-upload-file');
                              const docTypeSelect = document.getElementById('admin-upload-doc-type');
                              if (fileInput.files[0]) {
                                uploadDocumentForClient(selectedIntake.id, fileInput.files[0], docTypeSelect.value);
                                fileInput.value = '';
                                docTypeSelect.value = '';
                              } else {
                                toast.error("Please select a file first");
                              }
                            }}
                            className="bg-gold text-navy hover:bg-gold-hover"
                            size="sm"
                            data-testid="btn-admin-upload-doc"
                          >
                            <Upload size={16} className="mr-1" />
                            Upload
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-500">Client Notes:</span>
                    <Textarea
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      className="mt-2"
                      placeholder="Add internal notes about this client..."
                      data-testid="client-note-textarea"
                    />
                    <div className="mt-2 flex justify-end">
                      <Button
                        onClick={saveNote}
                        disabled={savingNote}
                        className="bg-navy hover:bg-navy-light"
                        data-testid="save-note-btn"
                      >
                        {savingNote ? "Saving..." : "Save Note"}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center pt-4 border-t">
                <div className="text-sm text-slate-500">
                  Submitted: {formatDate(selectedIntake.created_at)}
                </div>
                <StatusBadge status={selectedIntake.status} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reschedule Modal */}
      <Dialog open={rescheduleOpen} onOpenChange={setRescheduleOpen}>
        <DialogContent className="max-w-md z-[100]" data-testid="reschedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-navy text-xl">
              Reschedule Appointment
            </DialogTitle>
          </DialogHeader>
          
          {rescheduleAppt && (
            <div className="space-y-4 mt-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-semibold text-navy mb-2">Appointment Details</h4>
                <p className="text-sm text-slate-600">
                  <strong>Client:</strong> {rescheduleAppt.client_name}
                </p>
                <p className="text-sm text-slate-600">
                  <strong>Email:</strong> {rescheduleAppt.client_email}
                </p>
                <p className="text-sm text-slate-600">
                  <strong>Current Time:</strong> {rescheduleAppt.start_time 
                    ? estDateTime(rescheduleAppt.start_time)
                    : "Not set"}
                </p>
              </div>

              <div className="space-y-3">
                <div>
                  <Label htmlFor="reschedule-date" className="text-sm font-medium">
                    New Date
                  </Label>
                  <Popover open={rescheduleCalendarOpen} onOpenChange={setRescheduleCalendarOpen} modal={true}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal mt-1"
                        data-testid="reschedule-date-picker"
                        type="button"
                      >
                        {rescheduleDate ? rescheduleDate.toDateString() : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 z-[9999]" align="start" sideOffset={4} side="bottom" forceMount>
                      <Calendar
                        mode="single"
                        selected={rescheduleDate}
                        onSelect={(d) => {
                          if (!d) return;
                          setRescheduleDate(d);
                          setRescheduleCalendarOpen(false);
                        }}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <Label htmlFor="reschedule-time" className="text-sm font-medium">
                    New Time
                  </Label>
                  <Select value={rescheduleTime} onValueChange={setRescheduleTime}>
                    <SelectTrigger className="mt-1" data-testid="reschedule-time-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[10000]">
                      {appointmentSlots.map((slot) => (
                        <SelectItem key={slot} value={slot}>
                          {slot}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setRescheduleOpen(false)}
                  className="flex-1"
                  data-testid="reschedule-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveReschedule}
                  disabled={savingReschedule || !rescheduleDate}
                  className="flex-1 bg-navy hover:bg-navy-light"
                  data-testid="reschedule-save-btn"
                >
                  {savingReschedule ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Appointment Modal */}
      <Dialog open={newApptModalOpen} onOpenChange={setNewApptModalOpen}>
        <DialogContent className="max-w-md z-[100]" data-testid="new-appointment-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-navy text-xl">
              Schedule New Appointment
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="bg-gold/10 rounded-lg p-4">
              <p className="text-sm font-medium text-navy">
                📅 {newApptDate ? newApptDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'No date selected'}
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-sm font-medium">Client Name *</Label>
                <Input
                  value={newApptClientName}
                  onChange={(e) => setNewApptClientName(e.target.value)}
                  placeholder="John Doe"
                  className="mt-1"
                  data-testid="new-appt-client-name"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium">Client Email *</Label>
                <Input
                  type="email"
                  value={newApptClientEmail}
                  onChange={(e) => setNewApptClientEmail(e.target.value)}
                  placeholder="john@example.com"
                  className="mt-1"
                  data-testid="new-appt-client-email"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium">Client Phone</Label>
                <Input
                  value={newApptClientPhone}
                  onChange={(e) => setNewApptClientPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="mt-1"
                  data-testid="new-appt-client-phone"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium">Time *</Label>
                <Select value={newApptTime} onValueChange={setNewApptTime}>
                  <SelectTrigger className="mt-1" data-testid="new-appt-time-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[10000]">
                    {appointmentSlots.map((slot) => (
                      <SelectItem key={slot} value={slot}>
                        {slot}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium">Appointment Notes</Label>
                <Textarea
                  value={calendarNotes}
                  onChange={(e) => setCalendarNotes(e.target.value)}
                  placeholder="Add notes for this appointment (e.g., services needed, special requests)..."
                  className="mt-1 min-h-[80px]"
                  data-testid="new-appt-notes"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setNewApptModalOpen(false);
                  setNewApptClientName("");
                  setNewApptClientEmail("");
                  setNewApptClientPhone("");
                }}
                className="flex-1"
                data-testid="new-appt-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={createNewAppointment}
                disabled={savingNewAppt || !newApptClientName || !newApptClientEmail}
                className="flex-1 bg-navy hover:bg-navy-light"
                data-testid="new-appt-save-btn"
              >
                {savingNewAppt ? "Creating..." : "Create Appointment"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Preview Modal */}
      <Dialog open={exportPreviewOpen} onOpenChange={setExportPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-navy font-heading text-xl">
              Export Preview - {exportPreviewData.length} Clients
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-auto">
            <p className="text-sm text-slate-500 mb-4">
              The following clients will be exported to a CrossLink-compatible CSV file with 39 fields including contact info, tax details, appointments, and more.
            </p>

            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Name</TableHead>
                    <TableHead className="font-semibold">Email</TableHead>
                    <TableHead className="font-semibold">Phone</TableHead>
                    <TableHead className="font-semibold">Filing Status</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Appointment</TableHead>
                    <TableHead className="font-semibold">Referral</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {exportPreviewData.map((client) => (
                    <TableRow key={client.id}>
                      <TableCell className="font-medium">
                        {client.first_name} {client.last_name}
                      </TableCell>
                      <TableCell className="text-sm">{client.email}</TableCell>
                      <TableCell className="text-sm">{client.phone}</TableCell>
                      <TableCell className="text-sm">{client.filing_status || "-"}</TableCell>
                      <TableCell>
                        <StatusBadge status={client.status} />
                      </TableCell>
                      <TableCell className="text-sm">
                        {client.appointment_date ? (
                          <span>{client.appointment_date} {client.appointment_time}</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {client.referral_name || <span className="text-slate-400">-</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-600">
                <strong>Exported Fields (37 total):</strong> Address, Appointment Date/Time, Bank Info, City, Date of Birth, Dependents, Email, Filing Status, Income Sources (W2, 1099, Crypto, etc.), Name, Notes, Phone, Referral Name, SSN (last 4), Services, Spouse Info, State, Status, Submitted Date, Tax Year, Zip
              </p>
              <p className="text-xs text-slate-500 mt-1 italic">
                Note: Fees and Referral Rewards are internal-only and not included in the export.
              </p>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t mt-4">
            <Button
              variant="outline"
              onClick={() => setExportPreviewOpen(false)}
              className="flex-1"
              data-testid="export-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={downloadCsv}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              data-testid="export-download-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Modal */}
      <Dialog open={reviewModalOpen} onOpenChange={(open) => !open && closeReviewModal()}>
        <DialogContent className="max-w-lg" data-testid="review-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-navy text-xl">
              {editingReview ? "Edit Review" : "Add New Review"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Client Name *</Label>
                <Input
                  value={reviewForm.name}
                  onChange={(e) => setReviewForm({...reviewForm, name: e.target.value})}
                  placeholder="John D."
                  className="mt-1"
                  data-testid="review-name"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Location *</Label>
                <Input
                  value={reviewForm.location}
                  onChange={(e) => setReviewForm({...reviewForm, location: e.target.value})}
                  placeholder="Memphis, TN"
                  className="mt-1"
                  data-testid="review-location"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Rating</Label>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setReviewForm({...reviewForm, rating: star})}
                    className="focus:outline-none"
                    data-testid={`review-star-${star}`}
                  >
                    <Star 
                      size={24} 
                      className={`${star <= reviewForm.rating ? 'text-gold fill-gold' : 'text-slate-200'} transition-colors hover:text-gold`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Service Type</Label>
              <Select value={reviewForm.service} onValueChange={(val) => setReviewForm({...reviewForm, service: val})}>
                <SelectTrigger className="mt-1" data-testid="review-service">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {serviceOptions.map((service) => (
                    <SelectItem key={service} value={service}>
                      {service}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-medium">Review Text *</Label>
              <Textarea
                value={reviewForm.review}
                onChange={(e) => setReviewForm({...reviewForm, review: e.target.value})}
                placeholder="Share the client's experience..."
                className="mt-1 min-h-[100px]"
                data-testid="review-text"
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="is-featured"
                checked={reviewForm.is_featured}
                onCheckedChange={(checked) => setReviewForm({...reviewForm, is_featured: checked})}
                data-testid="review-featured"
              />
              <Label htmlFor="is-featured" className="text-sm cursor-pointer">
                Feature on landing page
              </Label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={closeReviewModal}
                className="flex-1"
                data-testid="review-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={saveReview}
                disabled={savingReview || !reviewForm.name || !reviewForm.location || !reviewForm.review}
                className="flex-1 bg-navy hover:bg-navy-light"
                data-testid="review-save-btn"
              >
                {savingReview ? "Saving..." : (editingReview ? "Update Review" : "Add Review")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ======================= MERGE PREVIEW MODAL ======================= */}
      <Dialog open={showMergeModal} onOpenChange={setShowMergeModal}>
        <DialogContent className="max-w-2xl" data-testid="merge-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-navy">
              <GitMerge className="w-5 h-5" />
              Merge Intakes
            </DialogTitle>
            <DialogDescription>
              Review the merge preview before combining these records.
            </DialogDescription>
          </DialogHeader>
          
          {mergePreview && (
            <div className="space-y-4 mt-4">
              {/* Master & Source Cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-green-700 font-semibold">🥇 MASTER (Keep)</span>
                  </div>
                  <p className="font-medium text-navy">{mergePreview.master.name}</p>
                  <p className="text-sm text-slate-600">{mergePreview.master.email}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Status: {mergePreview.master.status} | Docs: {mergePreview.master.document_count}
                  </p>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-amber-700 font-semibold">🥈 SOURCE (Merge & Archive)</span>
                  </div>
                  <p className="font-medium text-navy">{mergePreview.source.name}</p>
                  <p className="text-sm text-slate-600">{mergePreview.source.email}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Status: {mergePreview.source.status} | Docs: {mergePreview.source.actual_document_files || mergePreview.source.document_count}
                  </p>
                </div>
              </div>

              {/* What Will Be Merged */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-semibold text-navy mb-3">📋 What will be merged:</h4>
                
                {mergePreview.will_merge.personal_info.length > 0 && (
                  <div className="mb-3">
                    <p className="text-sm font-medium text-slate-700">Personal Info:</p>
                    <ul className="ml-4 text-sm text-slate-600">
                      {mergePreview.will_merge.personal_info.map((item, idx) => (
                        <li key={idx}>✓ {item.field}: {item.value}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {mergePreview.will_merge.tax_details.length > 0 && (
                  <div className="mb-3">
                    <p className="text-sm font-medium text-slate-700">Tax Details:</p>
                    <ul className="ml-4 text-sm text-slate-600">
                      {mergePreview.will_merge.tax_details.map((item, idx) => (
                        <li key={idx}>✓ {item.field}: {item.value}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {mergePreview.will_merge.documents.length > 0 && (
                  <div className="mb-3">
                    <p className="text-sm font-medium text-slate-700">Documents to Move:</p>
                    <ul className="ml-4 text-sm text-slate-600">
                      {mergePreview.will_merge.documents.map((doc, idx) => (
                        <li key={idx}>📄 {doc}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {mergePreview.will_merge.notes && (
                  <p className="text-sm text-slate-600">📝 Notes will be appended</p>
                )}
                
                {mergePreview.will_merge.personal_info.length === 0 && 
                 mergePreview.will_merge.tax_details.length === 0 && 
                 mergePreview.will_merge.documents.length === 0 && 
                 !mergePreview.will_merge.notes && (
                  <p className="text-sm text-slate-500 italic">No new data to merge. Source may be empty or identical.</p>
                )}
              </div>

              {/* Warning */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-800 text-sm">
                  ⚠️ After merge, the source intake will be <strong>DELETED</strong> and consolidated into the master file. This action cannot be undone.
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowMergeModal(false);
                    setMergePreview(null);
                    setSelectedMergeSource(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={executeMerge}
                  disabled={merging}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="confirm-merge-btn"
                >
                  {merging ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Merging...
                    </>
                  ) : (
                    <>
                      <GitMerge className="w-4 h-4 mr-2" />
                      Confirm Merge
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ======================= BACKUP CONFIRMATION CHECKLIST MODAL ======================= */}
      <Dialog open={showBackupChecklist} onOpenChange={setShowBackupChecklist}>
        <DialogContent className="max-w-lg" data-testid="backup-checklist-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-navy">
              <FileCheck className="w-5 h-5" />
              Backup Confirmation Checklist
            </DialogTitle>
            <DialogDescription>
              Complete all items before marking as "Filed"
            </DialogDescription>
          </DialogHeader>
          
          {backupChecklistIntake && (
            <div className="space-y-4 mt-4">
              {/* Client Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="font-medium text-blue-800">
                  📁 {backupChecklistIntake.contact?.first_name} {backupChecklistIntake.contact?.last_name}
                </p>
                <p className="text-sm text-blue-600">{backupChecklistIntake.contact?.email}</p>
              </div>

              {/* Checklist Items */}
              <div className="space-y-3">
                <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                  <input
                    type="checkbox"
                    checked={backupChecklist.folderCreated}
                    onChange={(e) => setBackupChecklist({...backupChecklist, folderCreated: e.target.checked})}
                    className="w-5 h-5 rounded border-slate-300 text-navy focus:ring-navy"
                  />
                  <span className="text-sm">Client folder created in Google Drive</span>
                </label>

                <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                  <input
                    type="checkbox"
                    checked={backupChecklist.documentsDownloaded}
                    onChange={(e) => setBackupChecklist({...backupChecklist, documentsDownloaded: e.target.checked})}
                    className="w-5 h-5 rounded border-slate-300 text-navy focus:ring-navy"
                  />
                  <span className="text-sm">Documents downloaded from Emergent</span>
                </label>

                <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                  <input
                    type="checkbox"
                    checked={backupChecklist.documentsUploaded}
                    onChange={(e) => setBackupChecklist({...backupChecklist, documentsUploaded: e.target.checked})}
                    className="w-5 h-5 rounded border-slate-300 text-navy focus:ring-navy"
                  />
                  <span className="text-sm">Documents uploaded to Drive</span>
                </label>

                <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                  <input
                    type="checkbox"
                    checked={backupChecklist.filesRenamed}
                    onChange={(e) => setBackupChecklist({...backupChecklist, filesRenamed: e.target.checked})}
                    className="w-5 h-5 rounded border-slate-300 text-navy focus:ring-navy"
                  />
                  <span className="text-sm">Files renamed correctly</span>
                </label>

                <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                  <input
                    type="checkbox"
                    checked={backupChecklist.linkSaved}
                    onChange={(e) => setBackupChecklist({...backupChecklist, linkSaved: e.target.checked})}
                    className="w-5 h-5 rounded border-slate-300 text-navy focus:ring-navy"
                  />
                  <span className="text-sm">Drive folder link saved externally</span>
                </label>

                {/* Google Drive Link Input */}
                <div className="p-3 bg-slate-50 rounded-lg">
                  <label className="text-sm font-medium text-slate-700 block mb-2">
                    Google Drive Folder Link <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={driveFolderLink}
                    onChange={(e) => setDriveFolderLink(e.target.value)}
                    placeholder="https://drive.google.com/drive/folders/..."
                    className="w-full"
                  />
                </div>
              </div>

              {/* Warning */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-amber-800 text-sm">
                  ⚠️ You <strong>cannot proceed</strong> to "Filed" until all boxes are checked and the Drive link is provided.
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowBackupChecklist(false);
                    setBackupChecklistIntake(null);
                    resetBackupChecklist();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={proceedToInPreparation}
                  disabled={!isBackupComplete()}
                  className={`${isBackupComplete() ? 'bg-green-600 hover:bg-green-700' : 'bg-slate-400'} text-white`}
                  data-testid="confirm-backup-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirm Backup & Proceed
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default AdminDashboard;
